// pointer-space-exempt: the sizes here are the PDF page's own dimensions
// (canvasSize, from the pdf.js viewport) multiplied by this tool's own zoom
// state - they are never derived from a pointer position, so there is no
// client-space number to convert. The clientX reads in this file drive
// hit-testing against that same viewport, not CSS lengths.
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { markupSignature, isMarkupDirty as isDirtyAgainst } from 'helpers/markupSignature';
import useSheetGrid from "components/Sheets/useSheetGrid";
import { paintSheetGrid } from "helpers/paintSheetGrid";
import { SHEET_GRID_WHY } from "api/glance/sheetGrid";

// Tools that collect points click-by-click.
const MULTI_POINT_TOOLS = ['polyline', 'polygon', 'area', 'perimeter'];
// Drag-to-draw shape tools (mousedown → mouseup).
const DRAG_TOOLS = ['line', 'arrow', 'leftArrow', 'doubleArrow', 'rectangle', 'highlightRect', 'circle', 'ellipse',
  'triangle', 'pentagon', 'hexagon', 'octagon', 'cloud', 'callout', 'cloudCallout', 'cross', 'checkmark', 'measure'];
// Predefined stamps (Bluebeam-style).
export const STAMPS = ['APPROVED', 'REJECTED', 'REVIEWED', 'FOR REVIEW', 'REVISE & RESUBMIT', 'AS BUILT', 'DRAFT', 'VOID', 'FINAL', 'CONFIDENTIAL'];
// Quick palette.
export const SWATCHES = ['#E53935', '#FB8C00', '#FDD835', '#43A047', '#1E88E5', '#8E24AA', '#000000', '#FFFFFF'];

// Tool catalogue for the flyouts (icon + label + hint).
const TOOL_META = {
  line: { group: 'lines', icon: '╱', label: 'Line', hint: 'L' },
  arrow: { group: 'lines', icon: '↗', label: 'Arrow', hint: 'A' },
  doubleArrow: { group: 'lines', icon: '⟷', label: 'Double arrow' },
  polyline: { group: 'lines', icon: '⌒', label: 'Polyline — click points, double-click / Enter to finish', hint: 'Q' },
  rectangle: { group: 'shapes', icon: '▭', label: 'Rectangle', hint: 'R' },
  highlightRect: { group: 'shapes', icon: '▮', label: 'Highlight area' },
  ellipse: { group: 'shapes', icon: '⬭', label: 'Ellipse', hint: 'E' },
  circle: { group: 'shapes', icon: '○', label: 'Circle' },
  cloud: { group: 'shapes', icon: '☁️', label: 'Cloud (revision)', hint: 'C' },
  polygon: { group: 'shapes', icon: '⬠', label: 'Polygon — click points, click first point to close', hint: 'O' },
  triangle: { group: 'shapes', icon: '△', label: 'Triangle' },
  checkmark: { group: 'shapes', icon: '✓', label: 'Checkmark' },
  cross: { group: 'shapes', icon: '✕', label: 'Cross' },
  measure: { group: 'measure', icon: '📏', label: 'Length', hint: 'M' },
  perimeter: { group: 'measure', icon: '⤳', label: 'Perimeter — click points, double-click to finish' },
  area: { group: 'measure', icon: '⬡', label: 'Area — click points, click first point to close' },
  count: { group: 'measure', icon: '①', label: 'Count — click to place numbered markers', hint: 'I' },
};

const flyItemStyle = { display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '4px 6px', fontSize: 12, cursor: 'pointer', borderRadius: 3 };
const flyInputStyle = { padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 3, fontSize: 11 };

function TbGroup({ children }) {
  return (
    <div style={{ display: 'flex', gap: 2, padding: 2, backgroundColor: 'white', borderRadius: 4, border: '1px solid #e5e7eb', alignItems: 'center' }}>
      {children}
    </div>
  );
}
function TbBtn({ active, onClick, title, disabled, children }) {
  return (
    <button onClick={onClick} title={title} disabled={disabled} style={{
      padding: '4px 8px', border: 'none', borderRadius: 3, cursor: disabled ? 'not-allowed' : 'pointer',
      backgroundColor: active ? '#3b82f6' : 'transparent', color: active ? 'white' : '#374151',
      fontSize: 13, lineHeight: '18px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      opacity: disabled ? 0.4 : 1, minWidth: 28,
    }}>{children}</button>
  );
}
// Button + dropdown panel; closes on outside click.
function TbFlyout({ id, openId, setOpenId, active, title, label, children }) {
  const ref = React.useRef(null);
  const open = openId === id;
  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpenId(null); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, setOpenId]);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpenId(open ? null : id)} title={title} style={{
        padding: '4px 8px', border: 'none', borderRadius: 3, cursor: 'pointer',
        backgroundColor: active ? '#3b82f6' : (open ? '#e0e7ff' : 'transparent'), color: active ? 'white' : '#374151',
        fontSize: 12, lineHeight: '18px', display: 'inline-flex', alignItems: 'center', gap: 4,
      }}>{label}<span style={{ fontSize: 9, opacity: 0.7 }}>▾</span></button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4, background: 'white', border: '1px solid #e5e7eb',
          borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 8, zIndex: 50, minWidth: 180,
        }}>
          {children}
        </div>
      )}
    </div>
  );
}
function FlyList({ items, tool, onPick }) {
  return (
    <div>
      {items.map((t) => {
        const m = TOOL_META[t] || { icon: '•', label: t };
        return (
          <button key={t} onClick={() => onPick(t)} title={m.label} style={{
            ...flyItemStyle, background: tool === t ? '#e0e7ff' : 'transparent', display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ width: 20, textAlign: 'center' }}>{m.icon}</span>
            <span style={{ flex: 1 }}>{m.label.split(' — ')[0]}</span>
            {m.hint && <kbd style={{ fontSize: 10, color: '#9ca3af' }}>{m.hint}</kbd>}
          </button>
        );
      })}
    </div>
  );
}

export default function IssueTool({
  isOpen, onClose, documentUrl, documentName, userInfo, onSaveIssue,
  embedded = false, hideHeader = false,
  // canEdit=false renders the annotated document read-only: toolbar, save
  // and all pointer/keyboard editing are disabled, markup still draws.
  canEdit = true,
  // Persisted issueObjects (issue.issueObjects) to re-hydrate the vector
  // shapes with. Entries whose data carries a shape `type` are seeded;
  // 3d-pins and other non-2D objects are ignored.
  initialIssueObjects = null,
  // Fit the document to the container once it loads (used by embedded
  // panes that show large screenshots without the zoom toolbar).
  fitOnLoad = false,
  // Optional ref the host page uses to trigger a save imperatively
  // (page-level "Save" button): saveRef.current = () => Promise.
  saveRef = null,
  // Optional full-screen control from the host page: {active, toggle}.
  // Renders a "Full screen" button above "Save markup" in the toolbar.
  fullscreen = null,
  // The Document Control sheet on the canvas, { projectId, docId, page? }:
  // with it, "Show grid" draws the project's building grid on the page (its
  // lettered and numbered lines) instead of the drawing lattice. page defaults
  // to the PDF page shown.
  sheetRef = null,
}) {
  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Canvas contexts
  const [ctx, setCtx] = useState(null);
  const [overlayCtx, setOverlayCtx] = useState(null);
  
  // Tool state
  const [tool, setTool] = useState('select');
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [currentPos, setCurrentPos] = useState({ x: 0, y: 0 });
  
  // Style state
  const [currentColor, setCurrentColor] = useState('#FF0000');
  const [fillColor, setFillColor] = useState('transparent');
  // How see-through a fill is. Kept in the colour itself (#rrggbbaa) so
  // every existing `context.fillStyle = shape.fillColor` keeps working -
  // colouring a zone on a drawing means seeing the drawing through it.
  const [fillAlpha, setFillAlpha] = useState(0.35);
  const withAlpha = (hex, a) => {
    const h = String(hex || '').trim();
    if (!/^#[0-9a-fA-F]{6}$/.test(h)) return h;
    return h + Math.round(Math.max(0, Math.min(1, a)) * 255).toString(16).padStart(2, '0');
  };
  const applyFill = (hex) => setFillColor(hex === 'transparent' ? 'transparent' : withAlpha(hex, fillAlpha));
  const [lineWidth, setLineWidth] = useState(2);
  const [fontSize, setFontSize] = useState(16);
  const [opacity, setOpacity] = useState(1);
  const [lineDash, setLineDash] = useState([]);
  
  // History
  const [history, setHistory] = useState([]);
  const [historyStep, setHistoryStep] = useState(-1);
  
  // View state
  const [scale, setScale] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  
  // Text input
  const [textInput, setTextInput] = useState('');
  const [textPosition, setTextPosition] = useState(null);
  
  // Grid
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [gridSize] = useState(20);
  
  // Measurement
  const [measurementUnit, setMeasurementUnit] = useState('px');
  const [pixelsPerInch, setPixelsPerInch] = useState(96);
  const [scaleFactor, setScaleFactor] = useState(1);
  
  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [issueUrl, setIssueUrl] = useState(null);
  
  // HAS ANYBODY ACTUALLY DRAWN ANYTHING?
  //
  // The host page's Save and Submit both run every mounted pane's saver,
  // and each save posts the composed canvas as a NEW image version. On an
  // issue with both a sheet pane and a screenshot pane that meant two
  // fresh copies of two unchanged pictures every time either button was
  // pressed, and an image history full of the same image.
  //
  // So the tool can answer "is my markup different from what was loaded",
  // and the host asks for a save that is skipped when it would be a no-op.
  //
  // Answered by COMPARING THE MARKUP ITSELF against a baseline taken when
  // the document loaded, not by watching for calls to setShapes. Watching
  // the setters was tried and got it wrong in the one flow that matters
  // most: pasting an image swaps the canvas document, several handlers
  // touch state on the way through, and the pane reported itself edited
  // when nobody had drawn a thing - so Submit filed the pasted picture a
  // second time, which is exactly the duplicate this was meant to stop.
  // A content comparison cannot be fooled that way: incidental state
  // churn that leaves the same shapes is not an edit, and neither is
  // drawing something and undoing it again.
  const baselineRef = useRef(null);
  const loadedDocRef = useRef(null);
  // True only for a user flipping sheets, which must NOT re-arm the
  // baseline (that would forget what was drawn on the sheet just left).
  const pageFlipRef = useRef(false);

  // Shapes and images - stored as objects with transform properties
  const [shapes, setShapes] = useState([]);
  // Multi-sheet documents. A controlled document is routinely several
  // sheets, and this viewer always rendered getPage(1) - so page 2
  // onwards simply did not exist anywhere in the app. Held here rather
  // than passed in, so every caller (Document Control, the areas page)
  // gets flipping without changing anything.
  const [pdfPage, setPdfPage] = useState(1);
  const sheetGrid = useSheetGrid(sheetRef && sheetRef.projectId, sheetRef && sheetRef.docId, (sheetRef && sheetRef.page) || pdfPage, showGrid);
  const [pdfPageCount, setPdfPageCount] = useState(1);
  // Markup is PER SHEET. The document-load effect below re-seeds shapes
  // every time it runs, and it runs on every page change - so without
  // this, flipping to sheet 2 and back threw away everything drawn on
  // sheet 1. Each page's work is stashed here on the way out and put
  // back on the way in. A ref, not state: it must be written and read
  // synchronously inside the flip, before the effect re-runs.
  const pageMarkupRef = useRef({});

  // Every sheet's markup as one comparable string - helpers/markupSignature.
  const signatureFrom = useCallback(
    (page, liveShapes, liveImages) => markupSignature(pageMarkupRef.current, page, liveShapes, liveImages),
    []
  );
  const [selectedShapeIndex, setSelectedShapeIndex] = useState(null);
  const [insertedImages, setInsertedImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  
  // Transform state
  const [isTransforming, setIsTransforming] = useState(false);
  const [transformType, setTransformType] = useState(null); // 'move', 'resize', 'rotate'
  const [transformHandle, setTransformHandle] = useState(null);
  
  // Transform panel visibility
  const [showTransformPanel, setShowTransformPanel] = useState(false);

  // Multi-point tools (polyline / polygon / area / perimeter): points
  // placed so far; finished on double-click, Enter, or clicking the
  // first point (closed shapes). Freehand strokes collect here too.
  const [draftPoints, setDraftPoints] = useState([]);
  // Stamp text picked from the Stamp flyout (placed on click).
  const [stampText, setStampText] = useState('APPROVED');
  // Count tool: next number.
  const [countNext, setCountNext] = useState(1);
  // What the text modal is creating: 'text' | 'note' | 'callout'.
  const [textKind, setTextKind] = useState('text');
  // Pending callout geometry (box drawn first, text asked after).
  const [pendingCallout, setPendingCallout] = useState(null);
  // Which toolbar flyout is open.
  const [openFlyout, setOpenFlyout] = useState(null);
  const pickTool = (t) => { setTool(t); setDraftPoints([]); setSelectedShapeIndex(null); setSelectedImageIndex(null); setShowTransformPanel(false); };

  // Base image data URL for history
  const [baseImageData, setBaseImageData] = useState(null);
  // Bumped when a page has FINISHED painting. The document render is
  // async: restoring a sheet's markup repaints the overlay immediately,
  // and then the raster lands, resizes both canvases and clears it -
  // so the markup was in state but invisible until the next edit. The
  // redraw effect waits on this too.
  const [renderTick, setRenderTick] = useState(0);

  // Latest initialIssueObjects without retriggering the doc-load effect
  const initialObjectsRef = useRef(initialIssueObjects);
  useEffect(() => { initialObjectsRef.current = initialIssueObjects; }, [initialIssueObjects]);

  // Keyboard support for delete and escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!canEdit) return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedImageIndex !== null) {
          e.preventDefault();
          setInsertedImages(prev => prev.filter((_, i) => i !== selectedImageIndex));
          setSelectedImageIndex(null);
        } else if (selectedShapeIndex !== null) {
          e.preventDefault();
          setShapes(prev => prev.filter((_, i) => i !== selectedShapeIndex));
          setSelectedShapeIndex(null);
        }
      }
      if (e.key === 'Escape') {
        setSelectedImageIndex(null);
        setSelectedShapeIndex(null);
        setShowTransformPanel(false);
        setDraftPoints([]);
        setPendingCallout(null);
      }
      // Finish a multi-point shape.
      if (e.key === 'Enter' && draftPointsRef.current.length >= 2 && MULTI_POINT_TOOLS.includes(toolRef.current)) {
        e.preventDefault();
        if (finishMultiPointRef.current) finishMultiPointRef.current();
      }
      // Single-key tool shortcuts (Bluebeam-style) — not while typing.
      const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName);
      if (typing || e.ctrlKey || e.metaKey || e.altKey) {
        if ((e.ctrlKey || e.metaKey) && !typing) {
          if (e.key.toLowerCase() === 'z' && !e.shiftKey) { e.preventDefault(); if (undoRef.current) undoRef.current(); }
          if (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey)) { e.preventDefault(); if (redoRef.current) redoRef.current(); }
        }
        return;
      }
      const map = { v: 'select', h: 'pan', p: 'pen', g: 'highlighter', x: 'eraser', t: 'text', n: 'note',
        l: 'line', a: 'arrow', r: 'rectangle', e: 'ellipse', c: 'cloud', k: 'cloudCallout', m: 'measure',
        q: 'polyline', o: 'polygon', s: 'stamp', i: 'count' };
      const next = map[e.key.toLowerCase()];
      if (next) { setTool(next); setDraftPoints([]); }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex, selectedShapeIndex, canEdit]);

  // Refs so the keydown effect (bound once) sees live values.
  const toolRef = useRef(tool); useEffect(() => { toolRef.current = tool; }, [tool]);
  const draftPointsRef = useRef([]); useEffect(() => { draftPointsRef.current = draftPoints; }, [draftPoints]);
  const finishMultiPointRef = useRef(null);
  const freehandRef = useRef([]);
  const undoRef = useRef(null);
  const redoRef = useRef(null);

  // Mouse wheel zoom
  useEffect(() => {
    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setScale(s => Math.max(0.3, Math.min(3, s + delta)));
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, []);

  // Load PDF document
  useEffect(() => {
    if (!isOpen || !canvasRef.current || !overlayCanvasRef.current || !documentUrl) return;

    const canvas = canvasRef.current;
    const overlayCanvas = overlayCanvasRef.current;
    const context = canvas.getContext('2d');
    const overlayContext = overlayCanvas.getContext('2d');
    
    setCtx(context);
    setOverlayCtx(overlayContext);

    context.clearRect(0, 0, canvas.width, canvas.height);
    overlayContext.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);

    // Per-sheet annotation state. Coming back to a sheet restores what
    // was drawn on it; arriving at one for the first time seeds it from
    // the persisted objects that belong to THAT sheet.
    // A DIFFERENT DOCUMENT IS NOT AN EDIT.
    //
    // The per-sheet stash belongs to the document being left, so it is
    // dropped here rather than only in the reset effect further down -
    // that one runs AFTER this, and the baseline taken below must not
    // count the previous document's sheets as markup on this one.
    const sameDocument = loadedDocRef.current === documentUrl;
    loadedDocRef.current = documentUrl;
    if (!sameDocument) pageMarkupRef.current = {};

    const stashed = pageMarkupRef.current[pdfPage];
    let seededShapes;
    let seededImages;
    if (stashed) {
      seededShapes = stashed.shapes || [];
      seededImages = stashed.images || [];
      if (typeof stashed.countNext === 'number') setCountNext(stashed.countNext);
    } else {
      // issue.issueObjects entries store the raw shape under .data.
      // Shapes saved before markup was per-sheet carry no page and
      // belong to sheet 1, which is where they were drawn.
      const seedObjects = Array.isArray(initialObjectsRef.current) ? initialObjectsRef.current : [];
      seededShapes = seedObjects
        .filter((o) => o && o.objectType !== '3d-pin' && o.data && typeof o.data.type === 'string')
        .map((o) => o.data)
        .filter((sh) => Number(sh.page || 1) === Number(pdfPage));
      seededImages = [];
    }
    setShapes(seededShapes);
    setInsertedImages(seededImages);
    // Arm the baseline against what was just loaded - on every run EXCEPT
    // a user flipping sheets, where the markup on the sheet being left is
    // real work that has to keep counting.
    if (!pageFlipRef.current) baselineRef.current = signatureFrom(pdfPage, seededShapes, seededImages);
    pageFlipRef.current = false;
    setSelectedShapeIndex(null);
    setSelectedImageIndex(null);

    const paintLoadError = () => {
      canvas.width = 1200;
      canvas.height = 1600;
      overlayCanvas.width = 1200;
      overlayCanvas.height = 1600;
      setCanvasSize({ width: 1200, height: 1600 });

      context.fillStyle = 'white';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.strokeStyle = '#ddd';
      context.strokeRect(0, 0, canvas.width, canvas.height);

      context.fillStyle = '#dc2626';
      context.font = '20px Arial';
      context.textAlign = 'center';
      context.fillText('Failed to load document', canvas.width / 2, canvas.height / 2 - 20);
      context.fillStyle = '#6b7280';
      context.font = '14px Arial';
      context.fillText('Please try again or contact support', canvas.width / 2, canvas.height / 2 + 10);

      setBaseImageData(canvas.toDataURL());
      saveToHistory();
    };

    // Plain raster documents (issue screenshots) skip pdf.js entirely.
    const isImageDoc =
      /^data:image\//i.test(documentUrl) ||
      /\.(png|jpe?g|gif|webp|bmp)(\?.*)?$/i.test(documentUrl);

    const loadImageAsDoc = (withCrossOrigin) => {
      const img = new Image();
      // Same-origin uploads are served with CORS (app.use(cors())), so an
      // anonymous load keeps the canvas exportable. If that load fails,
      // retry tainted so the viewer at least still sees the image.
      if (withCrossOrigin && !/^data:/i.test(documentUrl)) img.crossOrigin = 'anonymous';
      img.onload = () => {
        const w = img.naturalWidth || img.width || 1200;
        const h = img.naturalHeight || img.height || 800;
        canvas.width = w;
        canvas.height = h;
        overlayCanvas.width = w;
        overlayCanvas.height = h;
        setCanvasSize({ width: w, height: h });
        context.clearRect(0, 0, w, h);
        context.drawImage(img, 0, 0, w, h);
        try { setBaseImageData(canvas.toDataURL()); } catch (e) { setBaseImageData(null); }
        saveToHistory();
        setRenderTick((t) => t + 1);
      };
      img.onerror = () => {
        if (withCrossOrigin) { loadImageAsDoc(false); return; }
        paintLoadError();
      };
      img.src = documentUrl;
    };

    const loadPdfAsImage = async () => {
      try {
        if (!window.pdfjsLib) {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
          document.head.appendChild(script);
          
          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
          });
          
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        }

        const loadingTask = window.pdfjsLib.getDocument(documentUrl);
        const pdf = await loadingTask.promise;
        setPdfPageCount(pdf.numPages || 1);
        // Clamped: a document can be swapped for a shorter one while a
        // later page is selected.
        const wanted = Math.min(Math.max(1, pdfPage), pdf.numPages || 1);
        const page = await pdf.getPage(wanted);
        const viewport = page.getViewport({ scale: 1.5 });
        
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        overlayCanvas.width = viewport.width;
        overlayCanvas.height = viewport.height;

        setCanvasSize({ width: viewport.width, height: viewport.height });

        await page.render({ canvasContext: context, viewport }).promise;
        
        // Store base image
        setBaseImageData(canvas.toDataURL());
        saveToHistory();
        setRenderTick((t) => t + 1);
        
      } catch (error) {
        console.error('Error loading PDF:', error);
        paintLoadError();
      }
    };

    if (isImageDoc) {
      loadImageAsDoc(true);
    } else {
      loadPdfAsImage();
    }
  }, [isOpen, documentUrl, pdfPage]);

  // A different document starts at its own page one, with nobody else's
  // markup carried over.
  useEffect(() => {
    pageMarkupRef.current = {};
    setPdfPage(1);
    setPdfPageCount(1);
  }, [documentUrl]);

  // Stash the sheet being left, restore the one being entered. Undo
  // history is per sheet too and starts fresh - undoing across a sheet
  // boundary would repaint the wrong drawing.
  const flipPage = useCallback((next) => {
    const target = Math.max(1, Math.min(Number(next) || 1, pdfPageCount));
    if (target === pdfPage) return;
    pageMarkupRef.current[pdfPage] = {
      shapes: JSON.parse(JSON.stringify(shapes || [])),
      images: JSON.parse(JSON.stringify(insertedImages || [])),
      countNext,
    };
    setHistory([]);
    setHistoryStep(-1);
    pageFlipRef.current = true;
    setPdfPage(target);
  }, [pdfPage, pdfPageCount, shapes, insertedImages, countNext]);

  // Every sheet's markup, including the one on screen right now.
  // Callers need the sheet that has not been stashed yet, so it is
  // folded in here rather than relying on a flip having happened.
  const allPageShapes = useCallback(() => {
    const out = [];
    Object.keys(pageMarkupRef.current).forEach((k) => {
      const n = Number(k);
      if (n === pdfPage) return;                    // superseded by live state
      (pageMarkupRef.current[k].shapes || []).forEach((sh) => out.push({ ...sh, page: n }));
    });
    (shapes || []).forEach((sh) => out.push({ ...sh, page: pdfPage }));
    return out;
  }, [shapes, pdfPage]);

  // Optional one-shot fit once the document has a size - the same fit
  // the toolbar's Fit-to-screen button does. The container is a flex
  // child that can still be 0 x 0 when the image reports its size (the
  // pane mounts while the page lays out), and the old effect then gave
  // up for good: the media opened at 100 % and the person had to click
  // Fit. It now waits for the container to get a size and fits exactly
  // once per document.
  const fitDoneForRef = useRef("");
  useEffect(() => {
    if (!fitOnLoad || !canvasSize.width || !containerRef.current) return undefined;
    if (fitDoneForRef.current === documentUrl) return undefined;
    const container = containerRef.current;
    const fitNow = () => {
      if (!container.clientWidth || !container.clientHeight) return false;
      handleFitToScreen();
      fitDoneForRef.current = documentUrl;
      return true;
    };
    if (fitNow()) return undefined;
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => { if (fitNow()) ro.disconnect(); });
    ro.observe(container);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitOnLoad, canvasSize.width, canvasSize.height, documentUrl]);

  // Redraw everything when shapes or images change
  useEffect(() => {
    redrawCanvas();
  }, [shapes, insertedImages, selectedShapeIndex, selectedImageIndex, showGrid, sheetGrid, renderTick]);

  const redrawCanvas = useCallback(() => {
    if (!overlayCtx || !overlayCanvasRef.current) return;
    
    const canvas = overlayCanvasRef.current;
    overlayCtx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw grid if enabled
    if (showGrid) {
      drawGrid(overlayCtx, canvas.width, canvas.height);
    }
    
    // Draw all shapes
    shapes.forEach((shape, index) => {
      drawShape(overlayCtx, shape, index === selectedShapeIndex);
    });
    
    // Draw all images
    insertedImages.forEach((img, index) => {
      drawImage(overlayCtx, img, index === selectedImageIndex);
    });
  }, [overlayCtx, shapes, insertedImages, selectedShapeIndex, selectedImageIndex, showGrid, sheetGrid]);

  const drawGrid = (context, width, height) => {
    // The building grid when this page is placed on it (api/glance/sheetGrid,
    // page percent) - the same lines and labels as G in 3D. Otherwise the
    // drawing lattice, and the View menu says why.
    if (sheetGrid.lines.length) {
      paintSheetGrid(context, width, height, sheetGrid.lines);
      return;
    }
    context.strokeStyle = '#e0e0e0';
    context.lineWidth = 0.5;
    
    for (let x = 0; x <= width; x += gridSize) {
      context.beginPath();
      context.moveTo(x, 0);
      context.lineTo(x, height);
      context.stroke();
    }
    
    for (let y = 0; y <= height; y += gridSize) {
      context.beginPath();
      context.moveTo(0, y);
      context.lineTo(width, y);
      context.stroke();
    }
  };

  const snapToGridPoint = (x, y) => {
    if (!snapToGrid) return { x, y };
    return {
      x: Math.round(x / gridSize) * gridSize,
      y: Math.round(y / gridSize) * gridSize
    };
  };

  // Record a history entry for an explicit next state (setShapes is
  // async, so calling saveToHistory right after it captured the OLD
  // shapes and undo was one step behind).
  const commit = (nextShapes, nextImages) => {
    const ns = nextShapes ?? shapes;
    const ni = nextImages ?? insertedImages;
    if (nextShapes) setShapes(nextShapes);
    if (nextImages) setInsertedImages(nextImages);
    if (!canvasRef.current) return;
    let png = null;
    try { png = canvasRef.current.toDataURL(); } catch (e) { png = null; }
    const newHistory = history.slice(0, historyStep + 1);
    newHistory.push({ canvas: png, shapes: JSON.parse(JSON.stringify(ns)), images: JSON.parse(JSON.stringify(ni)) });
    setHistory(newHistory);
    setHistoryStep(newHistory.length - 1);
  };
  const addShape = (shape) => commit([...shapes, shape], null);

  const saveToHistory = () => {
    if (!canvasRef.current) return;
    // A cross-origin image without CORS taints the canvas and makes
    // toDataURL throw — skip history rather than crash the tool.
    try { canvasRef.current.toDataURL(); } catch (e) { return; }
    const newHistory = history.slice(0, historyStep + 1);
    newHistory.push({
      canvas: canvasRef.current.toDataURL(),
      shapes: JSON.parse(JSON.stringify(shapes)),
      images: JSON.parse(JSON.stringify(insertedImages))
    });
    setHistory(newHistory);
    setHistoryStep(newHistory.length - 1);
  };

  const restoreStep = (step) => {
    if (step.canvas && ctx && canvasRef.current) {
      const img = new window.Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.drawImage(img, 0, 0);
      };
      img.src = step.canvas;
    }
    if (step.shapes) setShapes(step.shapes);
    if (step.images) setInsertedImages(step.images);
    setSelectedShapeIndex(null);
    setSelectedImageIndex(null);
  };
  const undo = () => {
    if (historyStep > 0) {
      setHistoryStep(historyStep - 1);
      restoreStep(history[historyStep - 1]);
    }
  };
  const redo = () => {
    if (historyStep < history.length - 1) {
      setHistoryStep(historyStep + 1);
      restoreStep(history[historyStep + 1]);
    }
  };
  undoRef.current = undo;
  redoRef.current = redo;

  // Pointer -> canvas coordinates. The app renders under `html { zoom }`
  // (App.scss) and this canvas sits inside a CSS transform scale(), so
  // `clientX - rect.left` is in VISUAL px while the canvas draws in its own
  // CSS px: converting through the ratio of the on-screen box to the canvas
  // box is right for any zoom, transform scale and DPR (dividing by `scale`
  // alone was 20% short under the 0.8 zoom - strokes landed away from the
  // cursor and hit-tests for selection missed).
  const getMousePos = (e) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const cw = canvas.width || canvas.offsetWidth || rect.width;
    const ch = canvas.height || canvas.offsetHeight || rect.height;
    const x = rect.width ? (e.clientX - rect.left) * (cw / rect.width) : (e.clientX - rect.left) / scale;
    const y = rect.height ? (e.clientY - rect.top) * (ch / rect.height) : (e.clientY - rect.top) / scale;
    
    return snapToGridPoint(x, y);
  };
  // Visual px per layout px (page zoom), for scroll / pan deltas.
  const pageZoom = () => {
    const c = canvasRef.current;
    if (!c) return 1;
    const r = c.getBoundingClientRect();
    const layoutW = (c.offsetWidth || c.width) * scale;
    return layoutW && r.width ? r.width / layoutW : 1;
  };

  // Draw shape with transforms
// Draw shape with transforms (TEXT-AWARE CENTER FIX)
const drawShape = (context, shape, isSelected) => {
  context.save();

  // --------------------------------------------
  // Calculate center (special handling for text)
  // --------------------------------------------
  let centerX, centerY;

  if (shape.type === 'text') {
    // Measure text bounds to get true visual center
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');

    tempCtx.font = `${shape.fontSize || 16}px ${shape.fontFamily || 'Arial'}`;
    const metrics = tempCtx.measureText(shape.text || '');

    const textWidth = metrics.width;
    const textHeight = shape.fontSize || 16;

    centerX = shape.x + textWidth / 2;
    centerY = shape.y + textHeight / 2;
  } else if (Array.isArray(shape.points) && shape.points.length) {
    const b = pointsBounds(shape.points);
    centerX = b.x + b.width / 2;
    centerY = b.y + b.height / 2;
  } else if (shape.type === 'note' || shape.type === 'stamp') {
    centerX = shape.x + (shape.width || 0) / 2;
    centerY = shape.y + (shape.height || 0) / 2;
  } else {
    centerX =
      shape.centerX ??
      shape.x ??
      (shape.x1 !== undefined && shape.x2 !== undefined
        ? (shape.x1 + shape.x2) / 2
        : 0);

    centerY =
      shape.centerY ??
      shape.y ??
      (shape.y1 !== undefined && shape.y2 !== undefined
        ? (shape.y1 + shape.y2) / 2
        : 0);
  }

  // --------------------------------------------
  // Apply transforms
  // --------------------------------------------
  context.translate(centerX, centerY);
  context.rotate((shape.rotation || 0) * Math.PI / 180);
  context.scale(shape.scaleX || 1, shape.scaleY || 1);
  context.translate(-centerX, -centerY);

  // --------------------------------------------
  // Styles
  // --------------------------------------------
  context.strokeStyle = shape.color || currentColor;
  context.fillStyle = shape.fillColor || 'transparent';
  context.lineWidth = shape.lineWidth || lineWidth;
  context.globalAlpha = shape.opacity || 1;
  context.setLineDash(shape.lineDash || []);
  context.lineCap = 'round';
  context.lineJoin = 'round';

  // --------------------------------------------
  // Draw shape
  // --------------------------------------------
  switch (shape.type) {
    case 'text':
      drawText(context, shape);
      break;

    case 'line':
      context.beginPath();
      context.moveTo(shape.x1, shape.y1);
      context.lineTo(shape.x2, shape.y2);
      context.stroke();
      break;

    case 'leftArrow':
      drawLeftArrow(context, shape.x1, shape.y1, shape.x2, shape.y2);
      break;

    case 'doubleArrow':
      drawDoubleArrow(context, shape.x1, shape.y1, shape.x2, shape.y2);
      break;

    case 'rectangle':
      context.beginPath();
      context.rect(shape.x, shape.y, shape.width, shape.height);
      if (shape.fillColor !== 'transparent') context.fill();
      context.stroke();
      break;

    case 'circle':
      context.beginPath();
      context.arc(shape.x, shape.y, shape.radius, 0, Math.PI * 2);
      if (shape.fillColor !== 'transparent') context.fill();
      context.stroke();
      break;

    case 'ellipse':
      context.beginPath();
      context.ellipse(
        shape.x,
        shape.y,
        shape.radiusX,
        shape.radiusY,
        0,
        0,
        Math.PI * 2
      );
      if (shape.fillColor !== 'transparent') context.fill();
      context.stroke();
      break;

    case 'triangle':
      context.beginPath();
      context.moveTo(shape.x1, shape.y1);
      context.lineTo(shape.x2, shape.y2);
      context.lineTo(shape.x3, shape.y3);
      context.closePath();
      if (shape.fillColor !== 'transparent') context.fill();
      context.stroke();
      break;

    case 'pentagon':
    case 'hexagon':
    case 'octagon':
      drawPolygon(context, shape);
      break;

    case 'cloud':
      drawCloud(context, shape.x1, shape.y1, shape.x2, shape.y2);
      break;

    case 'callout':
      drawCallout(
        context,
        shape.x,
        shape.y,
        shape.width,
        shape.height,
        shape.pointerX,
        shape.pointerY,
        shape.text,
        shape.fontSize
      );
      break;

    case 'cloudCallout':
      drawCloudCallout(context, shape);
      break;

    case 'cross':
      drawCross(context, shape.x, shape.y, shape.size);
      break;

    case 'checkmark':
      drawCheckmark(context, shape.x, shape.y, shape.size);
      break;

    case 'measure':
      drawMeasurement(context, shape);
      break;

    case 'arrow':
      // Head at the END point (the usual "point at the problem" arrow).
      // drawLeftArrow's head is at its SECOND point despite the name.
      context.fillStyle = shape.color || currentColor;
      drawLeftArrow(context, shape.x1, shape.y1, shape.x2, shape.y2);
      break;

    case 'freehand':
      drawPath(context, shape.points, false, shape.highlight);
      break;

    case 'polyline':
      drawPath(context, shape.points, false, false);
      break;

    case 'polygon':
      drawPath(context, shape.points, true, false);
      if (shape.fillColor && shape.fillColor !== 'transparent') { context.fillStyle = shape.fillColor; context.fill(); }
      break;

    case 'perimeter':
      drawPath(context, shape.points, false, false);
      drawPathLabel(context, shape, `${convertPixelsToUnit(pathLength(shape.points, false))} ${unitLabelFor(shape.unit)}`);
      break;

    case 'area':
      drawPath(context, shape.points, true, false);
      context.save();
      context.globalAlpha = (shape.opacity || 1) * 0.15;
      context.fillStyle = shape.color || currentColor;
      context.fill();
      context.restore();
      drawPathLabel(context, shape, `${convertAreaToUnit(polygonArea(shape.points), shape.unit)}`);
      break;

    case 'note':
      drawNote(context, shape);
      break;

    case 'stamp':
      drawStamp(context, shape);
      break;

    case 'count':
      drawCount(context, shape);
      break;
  }

  // --------------------------------------------
  // Selection UI (NO transforms applied)
  // --------------------------------------------
  if (isSelected) {
    context.restore();
    context.save();

    const bounds = getShapeBounds(shape);
    context.strokeStyle = '#3b82f6';
    context.lineWidth = 2;
    context.setLineDash([5, 5]);
    context.globalAlpha = 1;

    context.strokeRect(
      bounds.x - 5,
      bounds.y - 5,
      bounds.width + 10,
      bounds.height + 10
    );

    drawTransformHandles(context, bounds, shape.rotation || 0);
  }

  context.restore();
};

  const drawText = (context, shape) => {
  context.font = `${shape.fontSize || fontSize}px ${shape.fontFamily || 'Arial'}`;
  context.fillStyle = shape.color || currentColor;
  context.textBaseline = 'top';
  context.fillText(shape.text, shape.x, shape.y);
};

  const drawTransformHandles = (context, bounds, rotation) => {
    const handleSize = 8;
    context.setLineDash([]);
    context.fillStyle = '#3b82f6';
    context.strokeStyle = 'white';
    context.lineWidth = 1;
    
    // Corner handles for resize
    const corners = [
      { x: bounds.x - 5, y: bounds.y - 5 },
      { x: bounds.x + bounds.width + 5, y: bounds.y - 5 },
      { x: bounds.x - 5, y: bounds.y + bounds.height + 5 },
      { x: bounds.x + bounds.width + 5, y: bounds.y + bounds.height + 5 },
    ];
    
    corners.forEach(corner => {
      context.fillRect(corner.x - handleSize / 2, corner.y - handleSize / 2, handleSize, handleSize);
      context.strokeRect(corner.x - handleSize / 2, corner.y - handleSize / 2, handleSize, handleSize);
    });
    
    // Rotation handle (circle above the shape)
    const rotateHandleY = bounds.y - 30;
    const rotateHandleX = bounds.x + bounds.width / 2;
    
    context.beginPath();
    context.arc(rotateHandleX, rotateHandleY, 6, 0, Math.PI * 2);
    context.fillStyle = '#10b981';
    context.fill();
    context.stroke();
    
    // Line connecting rotation handle
    context.beginPath();
    context.setLineDash([2, 2]);
    context.moveTo(rotateHandleX, rotateHandleY + 6);
    context.lineTo(rotateHandleX, bounds.y - 5);
    context.strokeStyle = '#10b981';
    context.stroke();
  };

  const drawImage = (context, img, isSelected) => {
    if (!img.imageData) return;
    
    const image = new window.Image();
    image.src = img.imageData;
    
    context.save();
    
    // Apply transforms
    const centerX = img.x + img.width / 2;
    const centerY = img.y + img.height / 2;
    
    context.translate(centerX, centerY);
    context.rotate((img.rotation || 0) * Math.PI / 180);
    context.scale(img.scaleX || 1, img.scaleY || 1);
    context.translate(-centerX, -centerY);
    
    context.globalAlpha = img.opacity || 1;
    context.drawImage(image, img.x, img.y, img.width, img.height);
    
    if (isSelected) {
      context.restore();
      context.save();
      
      context.strokeStyle = '#3b82f6';
      context.lineWidth = 2;
      context.setLineDash([5, 5]);
      context.strokeRect(img.x - 5, img.y - 5, img.width + 10, img.height + 10);
      
      drawTransformHandles(context, { x: img.x, y: img.y, width: img.width, height: img.height }, img.rotation || 0);
    }
    
    context.restore();
  };

  // Arrow drawing functions
const drawLeftArrow = (context, x1, y1, x2, y2) => {
  const headlen = 15;
  const angle = Math.atan2(y2 - y1, x2 - x1);

  // Draw line
  context.beginPath();
  context.moveTo(x1, y1);
  context.lineTo(x2, y2);
  context.stroke();

  // Draw arrowhead at END point (x2, y2) - pointing in direction of draw
  context.beginPath();
  context.moveTo(x2, y2);
  context.lineTo(x2 - headlen * Math.cos(angle - Math.PI / 6), y2 - headlen * Math.sin(angle - Math.PI / 6));
  context.lineTo(x2 - headlen * Math.cos(angle + Math.PI / 6), y2 - headlen * Math.sin(angle + Math.PI / 6));
  context.closePath();
  context.fill();
};
  const drawDoubleArrow = (context, x1, y1, x2, y2) => {
    const headlen = 15;
    const angle = Math.atan2(y2 - y1, x2 - x1);

    context.beginPath();
    context.moveTo(x1, y1);
    context.lineTo(x2, y2);
    context.stroke();

    // First arrowhead
    context.beginPath();
    context.moveTo(x1, y1);
    context.lineTo(x1 + headlen * Math.cos(angle + Math.PI / 6), y1 + headlen * Math.sin(angle + Math.PI / 6));
    context.lineTo(x1 + headlen * Math.cos(angle - Math.PI / 6), y1 + headlen * Math.sin(angle - Math.PI / 6));
    context.closePath();
    context.fill();

    // Second arrowhead
    context.beginPath();
    context.moveTo(x2, y2);
    context.lineTo(x2 - headlen * Math.cos(angle - Math.PI / 6), y2 - headlen * Math.sin(angle - Math.PI / 6));
    context.lineTo(x2 - headlen * Math.cos(angle + Math.PI / 6), y2 - headlen * Math.sin(angle + Math.PI / 6));
    context.closePath();
    context.fill();
  };

  const drawPolygon = (context, shape) => {
    const sides = shape.type === 'pentagon' ? 5 : shape.type === 'hexagon' ? 6 : 8;
    const radius = Math.sqrt(Math.pow(shape.x2 - shape.x1, 2) + Math.pow(shape.y2 - shape.y1, 2));
    const angle = Math.atan2(shape.y2 - shape.y1, shape.x2 - shape.x1);
    
    context.beginPath();
    for (let i = 0; i <= sides; i++) {
      const a = angle + (i * 2 * Math.PI) / sides;
      const x = shape.x1 + radius * Math.cos(a);
      const y = shape.y1 + radius * Math.sin(a);
      if (i === 0) context.moveTo(x, y);
      else context.lineTo(x, y);
    }
    context.closePath();
    if (shape.fillColor !== 'transparent') context.fill();
    context.stroke();
  };

  // Revision cloud: uniform outward scallops around an ellipse. Each
  // scallop is a real arc (not a bezier wobble) between consecutive
  // boundary points; the bulge direction is picked away from the centre.
  const drawCloud = (context, x1, y1, x2, y2) => {
    const w = Math.abs(x2 - x1), h = Math.abs(y2 - y1);
    if (w < 4 || h < 4) return;
    const cx = Math.min(x1, x2) + w / 2, cy = Math.min(y1, y2) + h / 2;
    const rx = w / 2, ry = h / 2;
    // scallop size scales with the shape but stays readable
    const perimeter = Math.PI * (3 * (rx + ry) - Math.sqrt((3 * rx + ry) * (rx + 3 * ry)));
    const scallop = Math.max(10, Math.min(28, perimeter / 14));
    const n = Math.max(8, Math.round(perimeter / scallop));
    const pts = [];
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      pts.push({ x: cx + rx * Math.cos(a), y: cy + ry * Math.sin(a) });
    }
    context.beginPath();
    for (let i = 0; i < n; i++) {
      const A = pts[i], B = pts[(i + 1) % n];
      const mx = (A.x + B.x) / 2, my = (A.y + B.y) / 2;
      const r = Math.hypot(B.x - A.x, B.y - A.y) / 2;
      const a0 = Math.atan2(A.y - my, A.x - mx), a1 = Math.atan2(B.y - my, B.x - mx);
      // Pick the sweep direction whose arc midpoint lies farther from the
      // ellipse centre (= bulges outward). Canvas arc(): anticlockwise=false
      // sweeps with increasing angle from a0 to a1.
      const TWO_PI = Math.PI * 2;
      const cwMid = a0 + (((a1 - a0) % TWO_PI) + TWO_PI) % TWO_PI / 2;
      const ccwMid = a0 - (((a0 - a1) % TWO_PI) + TWO_PI) % TWO_PI / 2;
      const dCw = Math.hypot(mx + r * Math.cos(cwMid) - cx, my + r * Math.sin(cwMid) - cy);
      const dCcw = Math.hypot(mx + r * Math.cos(ccwMid) - cx, my + r * Math.sin(ccwMid) - cy);
      if (i === 0) context.moveTo(A.x, A.y);
      context.arc(mx, my, r, a0, a1, dCcw > dCw);
    }
    context.closePath();
    context.stroke();
  };

  const drawCallout = (context, x, y, width, height, pointerX, pointerY, text, fs) => {
    // Box + leader line with an arrowhead at the pointer (Bluebeam callout).
    context.beginPath();
    context.rect(x, y, width, height);
    if (context.fillStyle !== 'transparent') context.fill();
    context.stroke();
    if (pointerX !== undefined && pointerY !== undefined) {
      // Leader starts at the box edge nearest the pointer.
      const cx = x + width / 2, cy = y + height / 2;
      const sx = pointerX < x ? x : pointerX > x + width ? x + width : cx;
      const sy = pointerY < y ? y : pointerY > y + height ? y + height : cy;
      context.save();
      context.fillStyle = context.strokeStyle;
      drawLeftArrow(context, sx, sy, pointerX, pointerY); // head at the pointer
      context.restore();
    }
    if (text) {
      context.save();
      context.setLineDash([]);
      context.fillStyle = context.strokeStyle;
      context.font = `${fs || 14}px Arial`;
      context.textBaseline = 'top';
      const lines = wrapText(context, text, width - 12);
      let ty = y + 6;
      for (const line of lines) { if (ty + (fs || 14) > y + height - 2) break; context.fillText(line, x + 6, ty); ty += (fs || 14) * 1.25; }
      context.restore();
    }
  };

// ---- points-based helpers (freehand / polyline / polygon / measurements) ----
const pointsBounds = (points) => {
  if (!points || !points.length) return { x: 0, y: 0, width: 0, height: 0 };
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of points) { if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x; if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y; }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};
const pathLength = (points, closed) => {
  let len = 0;
  for (let i = 1; i < points.length; i++) len += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  if (closed && points.length > 2) len += Math.hypot(points[0].x - points[points.length - 1].x, points[0].y - points[points.length - 1].y);
  return len;
};
const polygonArea = (points) => {
  let a = 0;
  for (let i = 0; i < points.length; i++) {
    const p = points[i], q = points[(i + 1) % points.length];
    a += p.x * q.y - q.x * p.y;
  }
  return Math.abs(a) / 2;
};
// Distance from a point to a polyline (for hit-testing thin paths).
const distToPath = (x, y, points, closed) => {
  let best = Infinity;
  const n = points.length;
  for (let i = 0; i < n - (closed ? 0 : 1); i++) {
    const a = points[i], b = points[(i + 1) % n];
    const dx = b.x - a.x, dy = b.y - a.y;
    const l2 = dx * dx + dy * dy || 1;
    let t = ((x - a.x) * dx + (y - a.y) * dy) / l2; t = Math.max(0, Math.min(1, t));
    const d = Math.hypot(x - (a.x + t * dx), y - (a.y + t * dy));
    if (d < best) best = d;
  }
  return best;
};

  const drawPath = (context, points, closed, highlight) => {
    if (!points || points.length < 2) {
      if (points && points.length === 1) { context.beginPath(); context.arc(points[0].x, points[0].y, (context.lineWidth || 2) / 2, 0, Math.PI * 2); context.fillStyle = context.strokeStyle; context.fill(); }
      return;
    }
    if (highlight) { context.globalAlpha = Math.min(context.globalAlpha, 0.35); context.lineWidth = Math.max(context.lineWidth * 3, 12); context.lineCap = 'butt'; }
    context.beginPath();
    context.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) context.lineTo(points[i].x, points[i].y);
    if (closed) context.closePath();
    context.stroke();
  };

  const unitLabelFor = (u) => ({ in: 'in', cm: 'cm', mm: 'mm', ft: 'ft', m: 'm', px: 'px' }[u || measurementUnit] || 'px');
  const convertAreaToUnit = (pxArea, unit) => {
    const u = unit || measurementUnit;
    const real = pxArea / (scaleFactor * scaleFactor);
    const perIn = pixelsPerInch * pixelsPerInch;
    switch (u) {
      case 'in': return `${(real / perIn).toFixed(1)} sq in`;
      case 'ft': return `${(real / perIn / 144).toFixed(2)} sq ft`;
      case 'cm': return `${(real / perIn * 6.4516).toFixed(1)} sq cm`;
      case 'mm': return `${(real / perIn * 645.16).toFixed(0)} sq mm`;
      case 'm': return `${(real / perIn * 0.00064516).toFixed(3)} sq m`;
      default: return `${Math.round(real)} px²`;
    }
  };

  // Label pill at the centroid of a points-based measurement.
  const drawPathLabel = (context, shape, text) => {
    const b = pointsBounds(shape.points || []);
    const cx = b.x + b.width / 2, cy = b.y + b.height / 2;
    context.save();
    context.setLineDash([]);
    context.globalAlpha = 1;
    context.font = `bold ${shape.fontSize || fontSize}px Arial`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    const w = context.measureText(text).width, pad = 6, fh = shape.fontSize || fontSize;
    context.fillStyle = 'rgba(255,255,255,0.95)';
    context.strokeStyle = shape.color || currentColor;
    context.lineWidth = 1;
    context.fillRect(cx - w / 2 - pad, cy - fh / 2 - pad, w + pad * 2, fh + pad * 2);
    context.strokeRect(cx - w / 2 - pad, cy - fh / 2 - pad, w + pad * 2, fh + pad * 2);
    context.fillStyle = shape.color || currentColor;
    context.fillText(text, cx, cy);
    context.restore();
  };

  // Sticky note: yellow card with wrapped text and a folded corner.
  const wrapText = (context, text, maxWidth) => {
    const out = [];
    for (const para of String(text || '').split('\n')) {
      let line = '';
      for (const word of para.split(' ')) {
        const test = line ? `${line} ${word}` : word;
        if (context.measureText(test).width > maxWidth && line) { out.push(line); line = word; }
        else line = test;
      }
      out.push(line);
    }
    return out;
  };
  const drawNote = (context, shape) => {
    const w = shape.width || 180, h = shape.height || 110, fs = shape.fontSize || 13;
    context.save();
    context.setLineDash([]);
    context.fillStyle = shape.fillColor && shape.fillColor !== 'transparent' ? shape.fillColor : '#FFF59D';
    context.strokeStyle = shape.color || '#F9A825';
    context.lineWidth = 1.5;
    context.shadowColor = 'rgba(0,0,0,0.18)'; context.shadowBlur = 6; context.shadowOffsetY = 2;
    context.beginPath();
    context.moveTo(shape.x, shape.y);
    context.lineTo(shape.x + w, shape.y);
    context.lineTo(shape.x + w, shape.y + h - 14);
    context.lineTo(shape.x + w - 14, shape.y + h);
    context.lineTo(shape.x, shape.y + h);
    context.closePath();
    context.fill();
    context.shadowColor = 'transparent';
    context.stroke();
    // folded corner
    context.beginPath();
    context.moveTo(shape.x + w - 14, shape.y + h);
    context.lineTo(shape.x + w - 14, shape.y + h - 14);
    context.lineTo(shape.x + w, shape.y + h - 14);
    context.fillStyle = 'rgba(0,0,0,0.08)';
    context.fill();
    // author + text
    context.fillStyle = '#5D4037';
    context.font = `bold ${Math.max(10, fs - 2)}px Arial`;
    context.textBaseline = 'top';
    if (shape.author) context.fillText(String(shape.author).slice(0, 28), shape.x + 8, shape.y + 6);
    context.font = `${fs}px Arial`;
    context.fillStyle = '#212121';
    const lines = wrapText(context, shape.text, w - 16);
    let y = shape.y + (shape.author ? 24 : 8);
    for (const line of lines) { if (y + fs > shape.y + h - 6) break; context.fillText(line, shape.x + 8, y); y += fs * 1.25; }
    context.restore();
  };

  // Rubber stamp: bold framed text, slightly rotated look via shape.rotation.
  const drawStamp = (context, shape) => {
    const w = shape.width || 160, h = shape.height || 40, fs = shape.fontSize || 18;
    context.save();
    context.setLineDash([]);
    context.strokeStyle = shape.color || '#C62828';
    context.fillStyle = shape.color || '#C62828';
    context.lineWidth = 3;
    context.globalAlpha = shape.opacity || 0.9;
    const r = 6;
    context.beginPath();
    context.moveTo(shape.x + r, shape.y);
    context.arcTo(shape.x + w, shape.y, shape.x + w, shape.y + h, r);
    context.arcTo(shape.x + w, shape.y + h, shape.x, shape.y + h, r);
    context.arcTo(shape.x, shape.y + h, shape.x, shape.y, r);
    context.arcTo(shape.x, shape.y, shape.x + w, shape.y, r);
    context.closePath();
    context.stroke();
    context.font = `bold ${fs}px Arial`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(String(shape.text || '').toUpperCase(), shape.x + w / 2, shape.y + h / 2);
    context.restore();
  };

  // Numbered count marker.
  const drawCount = (context, shape) => {
    const r = shape.radius || 13;
    context.save();
    context.setLineDash([]);
    context.beginPath();
    context.arc(shape.x, shape.y, r, 0, Math.PI * 2);
    context.fillStyle = shape.color || currentColor;
    context.fill();
    context.strokeStyle = '#fff';
    context.lineWidth = 2;
    context.stroke();
    context.fillStyle = '#fff';
    context.font = `bold ${Math.round(r * 1.05)}px Arial`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(String(shape.n ?? ''), shape.x, shape.y + 0.5);
    context.restore();
  };

  // Cloud callout (Bluebeam "Cloud+"): revision cloud with the text at
  // the START point; leader arrow from the cloud edge to the pointer with
  // the head at the END (destination).
  const cloudCalloutSize = (context, text, fs) => {
    context.save();
    context.font = `${fs || 14}px Arial`;
    const lines = wrapText(context, text || '', 220);
    let longest = 0;
    for (const l of lines) longest = Math.max(longest, context.measureText(l).width);
    context.restore();
    const w = Math.max(120, Math.min(280, longest + 44));
    const h = Math.max(64, lines.length * (fs || 14) * 1.25 + 34);
    return { w, h, lines };
  };
  const drawCloudCallout = (context, shape) => {
    const fs = shape.fontSize || 14;
    const w = shape.width || 140, h = shape.height || 70;
    const x = shape.x - w / 2, y = shape.y - h / 2;
    // optional fill only when the user picked one (no white backdrop by default)
    if (shape.fillColor && shape.fillColor !== 'transparent') {
      context.save();
      context.beginPath();
      context.ellipse(shape.x, shape.y, w / 2, h / 2, 0, 0, Math.PI * 2);
      context.fillStyle = shape.fillColor;
      context.fill();
      context.restore();
    }
    context.strokeStyle = shape.color || currentColor;
    context.lineWidth = shape.lineWidth || lineWidth;
    drawCloud(context, x, y, x + w, y + h);
    // leader: from the cloud edge (towards the pointer) to the pointer, head at the pointer
    if (shape.pointerX !== undefined && shape.pointerY !== undefined) {
      const dx = shape.pointerX - shape.x, dy = shape.pointerY - shape.y;
      const ang = Math.atan2(dy, dx);
      // ellipse boundary point in that direction (+ cloud bump margin)
      const rx = w / 2 * 1.12, ry = h / 2 * 1.12;
      const ex = shape.x + rx * Math.cos(ang), ey = shape.y + ry * Math.sin(ang);
      if (Math.hypot(dx, dy) > Math.max(rx, ry)) {
        context.save();
        context.setLineDash([]);
        context.fillStyle = shape.color || currentColor;   // arrowhead in the stroke colour
        drawLeftArrow(context, ex, ey, shape.pointerX, shape.pointerY); // head at the pointer
        context.restore();
      }
    }
    if (shape.text) {
      context.save();
      context.setLineDash([]);
      context.fillStyle = shape.color || currentColor;
      context.font = `${fs}px Arial`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      const lines = wrapText(context, shape.text, w - 36);
      const total = lines.length * fs * 1.25;
      let ty = shape.y - total / 2 + fs * 0.62;
      for (const line of lines) { context.fillText(line, shape.x, ty); ty += fs * 1.25; }
      context.restore();
    }
  };

const drawCross = (context, x, y, size) => {
  const halfSize = size / 2;
  context.lineWidth = size / 8;
  context.lineCap = 'round';
  
  // Draw X shape (diagonal lines) instead of + shape
  // First diagonal: top-left to bottom-right
  context.beginPath();
  context.moveTo(x - halfSize, y - halfSize);
  context.lineTo(x + halfSize, y + halfSize);
  context.stroke();
  
  // Second diagonal: top-right to bottom-left
  context.beginPath();
  context.moveTo(x + halfSize, y - halfSize);
  context.lineTo(x - halfSize, y + halfSize);
  context.stroke();
};

  const drawCheckmark = (context, x, y, size) => {
    context.lineWidth = size / 8;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.beginPath();
    context.moveTo(x - size / 3, y);
    context.lineTo(x - size / 8, y + size / 3);
    context.lineTo(x + size / 2, y - size / 3);
    context.stroke();
  };

  const convertPixelsToUnit = (pixels) => {
    const realPixels = pixels / scaleFactor;
    
    switch (measurementUnit) {
      case 'in': return (realPixels / pixelsPerInch).toFixed(2);
      case 'cm': return (realPixels / pixelsPerInch * 2.54).toFixed(2);
      case 'mm': return (realPixels / pixelsPerInch * 25.4).toFixed(1);
      case 'ft': return (realPixels / pixelsPerInch / 12).toFixed(2);
      case 'm': return (realPixels / pixelsPerInch * 0.0254).toFixed(3);
      default: return Math.round(realPixels);
    }
  };

  const getUnitLabel = () => {
    const labels = { in: 'in', cm: 'cm', mm: 'mm', ft: 'ft', m: 'm', px: 'px' };
    return labels[measurementUnit] || 'px';
  };

  const drawMeasurement = (context, shape) => {
    const distance = Math.sqrt(Math.pow(shape.x2 - shape.x1, 2) + Math.pow(shape.y2 - shape.y1, 2));
    const measurement = convertPixelsToUnit(distance);
    const distanceText = `${measurement} ${getUnitLabel()}`;
    const angle = Math.atan2(shape.y2 - shape.y1, shape.x2 - shape.x1);
    const capLen = 10;

    context.beginPath();
    context.moveTo(shape.x1, shape.y1);
    context.lineTo(shape.x2, shape.y2);
    context.stroke();

    // End caps
    context.beginPath();
    context.moveTo(shape.x1 - capLen * Math.sin(angle), shape.y1 + capLen * Math.cos(angle));
    context.lineTo(shape.x1 + capLen * Math.sin(angle), shape.y1 - capLen * Math.cos(angle));
    context.stroke();

    context.beginPath();
    context.moveTo(shape.x2 - capLen * Math.sin(angle), shape.y2 + capLen * Math.cos(angle));
    context.lineTo(shape.x2 + capLen * Math.sin(angle), shape.y2 - capLen * Math.cos(angle));
    context.stroke();

    // Text label
    context.save();
    context.font = `bold ${shape.fontSize || fontSize}px Arial`;
    context.fillStyle = shape.color;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    
    const textX = (shape.x1 + shape.x2) / 2;
    const textY = (shape.y1 + shape.y2) / 2;
    const textAngle = angle > Math.PI / 2 || angle < -Math.PI / 2 ? angle + Math.PI : angle;
    
    context.translate(textX, textY);
    context.rotate(textAngle);
    
    const textWidth = context.measureText(distanceText).width;
    const padding = 6;
    
    context.fillStyle = 'rgba(255, 255, 255, 0.95)';
    context.strokeStyle = shape.color;
    context.lineWidth = 1;
    context.fillRect(-textWidth / 2 - padding, -(shape.fontSize || fontSize) / 2 - padding, 
                     textWidth + padding * 2, (shape.fontSize || fontSize) + padding * 2);
    context.strokeRect(-textWidth / 2 - padding, -(shape.fontSize || fontSize) / 2 - padding, 
                       textWidth + padding * 2, (shape.fontSize || fontSize) + padding * 2);
    
    context.fillStyle = shape.color;
    context.fillText(distanceText, 0, 0);
    context.restore();
  };

const getShapeBounds = (shape) => {
  switch (shape.type) {
    case 'text':
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.font = `${shape.fontSize || 16}px ${shape.fontFamily || 'Arial'}`;
      const textMetrics = tempCtx.measureText(shape.text || '');
      const textWidth = textMetrics.width * (shape.scaleX || 1);
      const textHeight = (shape.fontSize || 16) * (shape.scaleY || 1);
      return {
        x: shape.x,
        y: shape.y,
        width: textWidth,
        height: textHeight
      };
    case 'freehand':
    case 'polyline':
    case 'polygon':
    case 'perimeter':
    case 'area': {
      const b = pointsBounds(shape.points || []);
      const pad = shape.highlight ? 8 : 4;
      return { x: b.x - pad, y: b.y - pad, width: b.width + pad * 2, height: b.height + pad * 2 };
    }
    case 'note':
      return { x: shape.x, y: shape.y, width: shape.width || 180, height: shape.height || 110 };
    case 'stamp':
      return { x: shape.x, y: shape.y, width: shape.width || 160, height: shape.height || 40 };
    case 'count': {
      const r = shape.radius || 13;
      return { x: shape.x - r, y: shape.y - r, width: r * 2, height: r * 2 };
    }
    case 'arrow':
    case 'leftArrow':
    case 'doubleArrow':
    case 'line':
    case 'measure':
      return {
        x: Math.min(shape.x1, shape.x2) - 10,
        y: Math.min(shape.y1, shape.y2) - 10,
        width: Math.abs(shape.x2 - shape.x1) + 20,
        height: Math.abs(shape.y2 - shape.y1) + 20
      };
    case 'rectangle':
      return { x: shape.x, y: shape.y, width: shape.width, height: shape.height };
    case 'cloudCallout': {
      const w = (shape.width || 140) * 1.15, h = (shape.height || 70) * 1.15;
      const minX = Math.min(shape.x - w / 2, shape.pointerX ?? shape.x), minY = Math.min(shape.y - h / 2, shape.pointerY ?? shape.y);
      const maxX = Math.max(shape.x + w / 2, shape.pointerX ?? shape.x), maxY = Math.max(shape.y + h / 2, shape.pointerY ?? shape.y);
      return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    }
    case 'callout': {
      const minX = Math.min(shape.x, shape.pointerX ?? shape.x), minY = Math.min(shape.y, shape.pointerY ?? shape.y);
      const maxX = Math.max(shape.x + shape.width, shape.pointerX ?? shape.x), maxY = Math.max(shape.y + shape.height, shape.pointerY ?? shape.y);
      return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    }
    case 'circle':
      return {
        x: shape.x - shape.radius,
        y: shape.y - shape.radius,
        width: shape.radius * 2,
        height: shape.radius * 2
      };
    case 'ellipse':
      return {
        x: shape.x - shape.radiusX,
        y: shape.y - shape.radiusY,
        width: shape.radiusX * 2,
        height: shape.radiusY * 2
      };
    case 'cross':
    case 'checkmark':
      return {
        x: shape.x - shape.size / 2,
        y: shape.y - shape.size / 2,
        width: shape.size,
        height: shape.size
      };
    case 'triangle':
      const minX = Math.min(shape.x1, shape.x2, shape.x3);
      const maxX = Math.max(shape.x1, shape.x2, shape.x3);
      const minY = Math.min(shape.y1, shape.y2, shape.y3);
      const maxY = Math.max(shape.y1, shape.y2, shape.y3);
      return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    case 'pentagon':
    case 'hexagon':
    case 'octagon':
    case 'cloud':
      const radius = Math.sqrt(Math.pow(shape.x2 - shape.x1, 2) + Math.pow(shape.y2 - shape.y1, 2));
      return {
        x: shape.x1 - radius,
        y: shape.y1 - radius,
        width: radius * 2,
        height: radius * 2
      };
    default:
      return { x: 0, y: 0, width: 0, height: 0 };
  }
};

  const getShapeAtPosition = (x, y) => {
    for (let i = shapes.length - 1; i >= 0; i--) {
      const sh = shapes[i];
      const bounds = getShapeBounds(sh);
      if (x >= bounds.x && x <= bounds.x + bounds.width &&
          y >= bounds.y && y <= bounds.y + bounds.height) {
        // Thin open paths: only when actually near the stroke, so a
        // big freehand scribble doesn't swallow clicks inside its box.
        if (['freehand', 'polyline', 'perimeter', 'line', 'arrow', 'leftArrow', 'doubleArrow', 'measure'].includes(sh.type)) {
          const pts = sh.points || [{ x: sh.x1, y: sh.y1 }, { x: sh.x2, y: sh.y2 }];
          const tol = Math.max(8, (sh.lineWidth || 2) * (sh.highlight ? 2 : 1.5));
          if (distToPath(x, y, pts, false) > tol) continue;
        }
        return i;
      }
    }
    return null;
  };

  const getImageAtPosition = (x, y) => {
    for (let i = insertedImages.length - 1; i >= 0; i--) {
      const img = insertedImages[i];
      if (x >= img.x && x <= img.x + img.width && y >= img.y && y <= img.y + img.height) {
        return i;
      }
    }
    return null;
  };

  const getTransformHandle = (x, y, bounds, rotation) => {
    const handleSize = 12;
    
    // Check rotation handle
    const rotateHandleY = bounds.y - 30;
    const rotateHandleX = bounds.x + bounds.width / 2;
    if (Math.sqrt(Math.pow(x - rotateHandleX, 2) + Math.pow(y - rotateHandleY, 2)) < handleSize) {
      return 'rotate';
    }
    
    // Check resize handles
    const corners = [
      { x: bounds.x - 5, y: bounds.y - 5, type: 'nw' },
      { x: bounds.x + bounds.width + 5, y: bounds.y - 5, type: 'ne' },
      { x: bounds.x - 5, y: bounds.y + bounds.height + 5, type: 'sw' },
      { x: bounds.x + bounds.width + 5, y: bounds.y + bounds.height + 5, type: 'se' },
    ];
    
    for (const corner of corners) {
      if (Math.abs(x - corner.x) < handleSize && Math.abs(y - corner.y) < handleSize) {
        return corner.type;
      }
    }
    
    return null;
  };

  const handleMouseDown = (e) => {
    if (!canEdit) return;
    if (!ctx) return;
    const pos = getMousePos(e);
    
    if (tool === 'pan') {
      setIsPanning(true);
      setStartPos({ 
        x: e.clientX, 
        y: e.clientY,
        scrollLeft: containerRef.current.scrollLeft,
        scrollTop: containerRef.current.scrollTop
      });
      return;
    }

    if (tool === 'text' || tool === 'note') {
      setTextKind(tool);
      setTextPosition(pos);
      return;
    }

    if (tool === 'stamp') {
      const tmp = document.createElement('canvas').getContext('2d');
      tmp.font = 'bold 18px Arial';
      const w = Math.max(90, tmp.measureText(String(stampText).toUpperCase()).width + 28);
      addShape({ type: 'stamp', x: pos.x - w / 2, y: pos.y - 20, width: w, height: 40, text: stampText,
        color: currentColor, opacity, fontSize: 18, rotation: -8, scaleX: 1, scaleY: 1 });
      return;
    }

    if (tool === 'count') {
      addShape({ type: 'count', x: pos.x, y: pos.y, radius: 13, n: countNext, color: currentColor, opacity: 1, rotation: 0, scaleX: 1, scaleY: 1 });
      setCountNext((n) => n + 1);
      return;
    }

    if (MULTI_POINT_TOOLS.includes(tool)) {
      // Click near the first point closes polygon/area; otherwise add a vertex.
      const closedKind = tool === 'polygon' || tool === 'area';
      if (closedKind && draftPoints.length >= 3 && Math.hypot(pos.x - draftPoints[0].x, pos.y - draftPoints[0].y) < 10 / scale + 6) {
        finishMultiPoint();
        return;
      }
      setDraftPoints((prev) => [...prev, pos]);
      return;
    }

    if (tool === 'eyedropper') {
      const imageData = ctx.getImageData(pos.x, pos.y, 1, 1);
      const [r, g, b] = imageData.data;
      setCurrentColor(`#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`);
      return;
    }


if (tool === 'select') {
  // FIRST: Check if clicking on transform handles of currently selected element
  // This must happen BEFORE checking getImageAtPosition/getShapeAtPosition
  // because transform handles (especially rotation) are OUTSIDE the shape bounds
  
  if (selectedImageIndex !== null) {
    const img = insertedImages[selectedImageIndex];
    const bounds = { x: img.x, y: img.y, width: img.width, height: img.height };
    const handle = getTransformHandle(pos.x, pos.y, bounds, img.rotation || 0);
    
    if (handle) {
      // Clicked on a transform handle of selected image
      setIsTransforming(true);
      setTransformType(handle === 'rotate' ? 'rotate' : 'resize');
      setTransformHandle(handle);
      setStartPos(pos);
      setIsDrawing(true);
      return;
    }
    
    // Check if clicking inside the selected image (for moving)
    if (pos.x >= img.x && pos.x <= img.x + img.width && 
        pos.y >= img.y && pos.y <= img.y + img.height) {
      setIsTransforming(true);
      setTransformType('move');
      setStartPos(pos);
      setIsDrawing(true);
      return;
    }
  }
  
  if (selectedShapeIndex !== null) {
    const shape = shapes[selectedShapeIndex];
    const bounds = getShapeBounds(shape);
    const handle = getTransformHandle(pos.x, pos.y, bounds, shape.rotation || 0);
    
    if (handle) {
      // Clicked on a transform handle of selected shape
      setIsTransforming(true);
      setTransformType(handle === 'rotate' ? 'rotate' : 'resize');
      setTransformHandle(handle);
      setStartPos(pos);
      setIsDrawing(true);
      return;
    }
    
    // Check if clicking inside the selected shape (for moving)
    if (pos.x >= bounds.x && pos.x <= bounds.x + bounds.width && 
        pos.y >= bounds.y && pos.y <= bounds.y + bounds.height) {
      setIsTransforming(true);
      setTransformType('move');
      setStartPos(pos);
      setIsDrawing(true);
      return;
    }
  }
  
  // SECOND: Check if clicking on a different image
  const imgIndex = getImageAtPosition(pos.x, pos.y);
  if (imgIndex !== null) {
    setSelectedImageIndex(imgIndex);
    setSelectedShapeIndex(null);
    setShowTransformPanel(true);
    setStartPos(pos);
    setIsDrawing(true);
    return;
  }
  
  // THIRD: Check if clicking on a different shape
  const shapeIndex = getShapeAtPosition(pos.x, pos.y);
  if (shapeIndex !== null) {
    setSelectedShapeIndex(shapeIndex);
    setSelectedImageIndex(null);
    setShowTransformPanel(true);
    setStartPos(pos);
    setIsDrawing(true);
    return;
  }
  
  // FOURTH: Clicked on empty space - deselect everything
  setSelectedImageIndex(null);
  setSelectedShapeIndex(null);
  setShowTransformPanel(false);
  return;
}

    if (tool === 'eraser') {
      // Vector eraser: remove the markup under the cursor (drag to keep
      // erasing). The underlying document is never touched.
      const imgIndex = getImageAtPosition(pos.x, pos.y);
      if (imgIndex !== null) { commit(null, insertedImages.filter((_, i) => i !== imgIndex)); }
      else {
        const idx = getShapeAtPosition(pos.x, pos.y);
        if (idx !== null) commit(shapes.filter((_, i) => i !== idx), null);
      }
      setSelectedShapeIndex(null);
      setSelectedImageIndex(null);
      setIsDrawing(true);
      return;
    }

    setIsDrawing(true);
    setStartPos(pos);
    setCurrentPos(pos);

    if (tool === 'pen' || tool === 'pencil' || tool === 'brush' || tool === 'highlighter') {
      // Vector freehand: points collect in a ref (mousemove fires faster
      // than React re-renders), previewed on the overlay, committed as
      // ONE editable shape on mouse-up.
      freehandRef.current = [pos];
    }
  };

  // Finish a polyline / polygon / area / perimeter from draftPoints.
  const finishMultiPoint = () => {
    const pts = draftPoints;
    const t = tool;
    setDraftPoints([]);
    if (!MULTI_POINT_TOOLS.includes(t) || pts.length < 2) return;
    if ((t === 'polygon' || t === 'area') && pts.length < 3) return;
    addShape({
      type: t, points: pts,
      // `area` is a closed shape too - filling it is the whole point when
      // you are colouring coordination zones on a sheet.
      color: currentColor, fillColor: (t === 'polygon' || t === 'area') ? fillColor : 'transparent',
      lineWidth, opacity, lineDash, fontSize, unit: measurementUnit,
      rotation: 0, scaleX: 1, scaleY: 1,
    });
  };
  finishMultiPointRef.current = finishMultiPoint;

  const handleDoubleClick = (e) => {
    if (!canEdit) return;
    if (MULTI_POINT_TOOLS.includes(tool) && draftPoints.length >= 2) {
      e.preventDefault();
      finishMultiPoint();
    }
  };

  const handleMouseMove = (e) => {
    if (!canEdit) return;
    const pos = getMousePos(e);
    
    if (isPanning && containerRef.current) {
      const z = pageZoom() || 1;
      const deltaX = (startPos.x - e.clientX) / z;
      const deltaY = (startPos.y - e.clientY) / z;
      containerRef.current.scrollLeft = startPos.scrollLeft + deltaX;
      containerRef.current.scrollTop = startPos.scrollTop + deltaY;
      return;
    }

    // Handle transforms for selected elements
    if (tool === 'select' && isDrawing && isTransforming) {
      const dx = pos.x - startPos.x;
      const dy = pos.y - startPos.y;
      
      if (selectedImageIndex !== null) {
        setInsertedImages(prev => {
          const newImages = [...prev];
          const img = { ...newImages[selectedImageIndex] };
          
          if (transformType === 'move') {
            img.x += dx;
            img.y += dy;
          } else if (transformType === 'rotate') {
            const centerX = img.x + img.width / 2;
            const centerY = img.y + img.height / 2;
            const angle = Math.atan2(pos.y - centerY, pos.x - centerX) * 180 / Math.PI + 90;
            img.rotation = angle;
          } else if (transformType === 'resize') {
            const aspectRatio = img.width / img.height;
            
            switch (transformHandle) {
              case 'se':
                img.width = Math.max(20, img.width + dx);
                img.height = Math.max(20, img.height + dy);
                break;
              case 'sw':
                img.x += dx;
                img.width = Math.max(20, img.width - dx);
                img.height = Math.max(20, img.height + dy);
                break;
              case 'ne':
                img.y += dy;
                img.width = Math.max(20, img.width + dx);
                img.height = Math.max(20, img.height - dy);
                break;
              case 'nw':
                img.x += dx;
                img.y += dy;
                img.width = Math.max(20, img.width - dx);
                img.height = Math.max(20, img.height - dy);
                break;
            }
          }
          
          newImages[selectedImageIndex] = img;
          return newImages;
        });
        setStartPos(pos);
        return;
      }
      
      if (selectedShapeIndex !== null) {
        setShapes(prev => {
          const newShapes = [...prev];
          const shape = { ...newShapes[selectedShapeIndex] };
          
          if (transformType === 'move') {
            // Move shape based on its type
            if (shape.x !== undefined) {
              shape.x += dx;
              shape.y += dy;
            }
            if (shape.x1 !== undefined) {
              shape.x1 += dx;
              shape.y1 += dy;
              shape.x2 += dx;
              shape.y2 += dy;
            }
            if (shape.x3 !== undefined) {
              shape.x3 += dx;
              shape.y3 += dy;
            }
            if (shape.pointerX !== undefined) {
              shape.pointerX += dx;
              shape.pointerY += dy;
            }
          } else if (transformType === 'rotate') {
            const bounds = getShapeBounds(shape);
            const centerX = bounds.x + bounds.width / 2;
            const centerY = bounds.y + bounds.height / 2;
            const angle = Math.atan2(pos.y - centerY, pos.x - centerX) * 180 / Math.PI + 90;
            shape.rotation = angle;
          } else if (transformType === 'resize') {
            // Scale shape
            const scaleX = (shape.scaleX || 1) + dx * 0.01;
            const scaleY = (shape.scaleY || 1) + dy * 0.01;
            shape.scaleX = Math.max(0.1, scaleX);
            shape.scaleY = Math.max(0.1, scaleY);
          }
          
          newShapes[selectedShapeIndex] = shape;
          return newShapes;
        });
        setStartPos(pos);
        return;
      }
    }

    if (!isDrawing || !ctx) {
      setCurrentPos(pos);
      return;
    }
    
    setCurrentPos(pos);

    if (tool === 'pen' || tool === 'pencil' || tool === 'brush' || tool === 'highlighter') {
      const last = freehandRef.current[freehandRef.current.length - 1];
      if (!last || Math.hypot(pos.x - last.x, pos.y - last.y) >= 1.5) freehandRef.current.push(pos);
      const next = freehandRef.current;
      redrawCanvas();
      if (overlayCtx) {
        overlayCtx.save();
        overlayCtx.strokeStyle = currentColor;
        overlayCtx.lineWidth = tool === 'brush' ? lineWidth * 2 : lineWidth;
        overlayCtx.globalAlpha = opacity;
        overlayCtx.lineCap = 'round'; overlayCtx.lineJoin = 'round';
        overlayCtx.setLineDash([]);
        drawPath(overlayCtx, next, false, tool === 'highlighter');
        overlayCtx.restore();
      }
      return;
    }

    if (tool === 'eraser') {
      const idx = getShapeAtPosition(pos.x, pos.y);
      if (idx !== null) commit(shapes.filter((_, i) => i !== idx), null);
      return;
    }

    // Draw preview for shape tools
    if (DRAG_TOOLS.includes(tool)) {
      redrawCanvas();
      drawPreviewShape(overlayCtx, tool, startPos, pos);
    }
  };

  // Multi-point rubber band (mouse move while NOT dragging).
  useEffect(() => {
    if (!overlayCtx) return;
    if (!MULTI_POINT_TOOLS.includes(tool) || draftPoints.length === 0) return;
    redrawCanvas();
    overlayCtx.save();
    overlayCtx.strokeStyle = currentColor;
    overlayCtx.lineWidth = lineWidth;
    overlayCtx.globalAlpha = opacity;
    overlayCtx.setLineDash(lineDash);
    overlayCtx.lineCap = 'round'; overlayCtx.lineJoin = 'round';
    drawPath(overlayCtx, [...draftPoints, currentPos], false, false);
    // vertices
    overlayCtx.setLineDash([]);
    for (const p of draftPoints) { overlayCtx.beginPath(); overlayCtx.arc(p.x, p.y, 4, 0, Math.PI * 2); overlayCtx.fillStyle = '#fff'; overlayCtx.fill(); overlayCtx.stroke(); }
    if ((tool === 'polygon' || tool === 'area') && draftPoints.length >= 3) {
      overlayCtx.beginPath(); overlayCtx.arc(draftPoints[0].x, draftPoints[0].y, 8, 0, Math.PI * 2); overlayCtx.strokeStyle = '#10b981'; overlayCtx.stroke();
    }
    overlayCtx.restore();
  }, [draftPoints, currentPos, tool]); // eslint-disable-line

  const drawPreviewShape = (context, toolType, start, end) => {
    context.save();
    context.strokeStyle = currentColor;
    context.fillStyle = fillColor;
    context.lineWidth = lineWidth;
    context.globalAlpha = opacity;
    context.setLineDash(lineDash);
    context.lineCap = 'round';
    context.lineJoin = 'round';

    const dx = end.x - start.x;
    const dy = end.y - start.y;

    switch (toolType) {
        case 'text':
  // Text preview is handled by the modal, not needed here
  break;
      case 'line':
        context.beginPath();
        context.moveTo(start.x, start.y);
        context.lineTo(end.x, end.y);
        context.stroke();
        break;
      case 'leftArrow':
        drawLeftArrow(context, start.x, start.y, end.x, end.y);
        break;
      case 'arrow':
        context.fillStyle = currentColor;
        drawLeftArrow(context, start.x, start.y, end.x, end.y);
        break;
      case 'doubleArrow':
        drawDoubleArrow(context, start.x, start.y, end.x, end.y);
        break;
      case 'highlightRect':
        context.globalAlpha = 0.35;
        context.fillStyle = currentColor;
        context.fillRect(start.x, start.y, dx, dy);
        break;
      case 'cloud':
        drawCloud(context, start.x, start.y, end.x, end.y);
        break;
      case 'callout':
        drawCallout(context, Math.min(start.x, end.x), Math.min(start.y, end.y), Math.abs(dx), Math.abs(dy), end.x, end.y + 50);
        break;
      case 'cloudCallout':
        drawCloudCallout(context, { x: start.x, y: start.y, width: 140, height: 70, pointerX: end.x, pointerY: end.y, color: currentColor, fontSize });
        break;
      case 'pentagon':
      case 'hexagon':
      case 'octagon':
        drawPolygon(context, { type: toolType, x1: start.x, y1: start.y, x2: end.x, y2: end.y, fillColor });
        break;
      case 'cross':
        drawCross(context, start.x, start.y, Math.hypot(dx, dy));
        break;
      case 'checkmark':
        drawCheckmark(context, start.x, start.y, Math.hypot(dx, dy));
        break;
      case 'rectangle':
        context.beginPath();
        context.rect(start.x, start.y, dx, dy);
        if (fillColor !== 'transparent') context.fill();
        context.stroke();
        break;
      case 'circle':
        const radius = Math.sqrt(dx * dx + dy * dy);
        context.beginPath();
        context.arc(start.x, start.y, radius, 0, Math.PI * 2);
        if (fillColor !== 'transparent') context.fill();
        context.stroke();
        break;
      case 'ellipse':
        context.beginPath();
        context.ellipse(start.x + dx / 2, start.y + dy / 2, Math.abs(dx / 2), Math.abs(dy / 2), 0, 0, Math.PI * 2);
        if (fillColor !== 'transparent') context.fill();
        context.stroke();
        break;
      case 'triangle':
        context.beginPath();
        context.moveTo(start.x + dx / 2, start.y);
        context.lineTo(start.x, start.y + dy);
        context.lineTo(start.x + dx, start.y + dy);
        context.closePath();
        if (fillColor !== 'transparent') context.fill();
        context.stroke();
        break;
      case 'measure':
        drawMeasurement(context, { x1: start.x, y1: start.y, x2: end.x, y2: end.y, color: currentColor, fontSize });
        break;
    }

    context.restore();
  };

  const handleMouseUp = (e) => {
    if (!canEdit) return;
    if (isPanning) {
      setIsPanning(false);
      return;
    }

    if (isTransforming) {
      setIsTransforming(false);
      setTransformType(null);
      setTransformHandle(null);
      saveToHistory();
    }

    if (tool === 'select' && isDrawing) {
      setIsDrawing(false);
      return;
    }

    if (!isDrawing || !ctx) return;
    
    const pos = getMousePos(e);

    if (tool === 'eraser') { setIsDrawing(false); return; }

    if (tool === 'pen' || tool === 'pencil' || tool === 'brush' || tool === 'highlighter') {
      const pts = freehandRef.current.length ? freehandRef.current : [pos];
      freehandRef.current = [];
      setIsDrawing(false);
      redrawCanvas();
      if (pts.length >= 1) {
        addShape({
          type: 'freehand', points: pts, highlight: tool === 'highlighter',
          color: currentColor, lineWidth: tool === 'brush' ? lineWidth * 2 : lineWidth,
          opacity: tool === 'highlighter' ? 0.35 : opacity, lineDash: [],
          rotation: 0, scaleX: 1, scaleY: 1,
        });
      }
      return;
    }
    
    // Create shape based on tool
    let newShape = null;
    
    switch (tool) {
      case 'arrow':
        newShape = {
          type: 'arrow',
          x1: startPos.x, y1: startPos.y, x2: pos.x, y2: pos.y,
          color: currentColor, fillColor: currentColor, lineWidth, opacity,
          rotation: 0, scaleX: 1, scaleY: 1
        };
        break;
      case 'highlightRect':
        newShape = {
          type: 'rectangle',
          x: Math.min(startPos.x, pos.x), y: Math.min(startPos.y, pos.y),
          width: Math.abs(pos.x - startPos.x), height: Math.abs(pos.y - startPos.y),
          color: currentColor, fillColor: currentColor, lineWidth: 1, opacity: 0.35,
          rotation: 0, scaleX: 1, scaleY: 1
        };
        break;
      case 'line':
        newShape = {
          type: 'line',
          x1: startPos.x, y1: startPos.y, x2: pos.x, y2: pos.y,
          color: currentColor, lineWidth, opacity, lineDash,
          rotation: 0, scaleX: 1, scaleY: 1
        };
        break;
      case 'leftArrow':
        newShape = {
          type: 'leftArrow',
          x1: startPos.x, y1: startPos.y, x2: pos.x, y2: pos.y,
          color: currentColor, fillColor: currentColor, lineWidth, opacity,
          rotation: 0, scaleX: 1, scaleY: 1
        };
        break;
      case 'doubleArrow':
        newShape = {
          type: 'doubleArrow',
          x1: startPos.x, y1: startPos.y, x2: pos.x, y2: pos.y,
          color: currentColor, fillColor: currentColor, lineWidth, opacity,
          rotation: 0, scaleX: 1, scaleY: 1
        };
        break;
      case 'rectangle':
        newShape = {
          type: 'rectangle',
          x: Math.min(startPos.x, pos.x), y: Math.min(startPos.y, pos.y),
          width: Math.abs(pos.x - startPos.x), height: Math.abs(pos.y - startPos.y),
          color: currentColor, fillColor, lineWidth, opacity,
          rotation: 0, scaleX: 1, scaleY: 1
        };
        break;
      case 'circle':
        const circleRadius = Math.sqrt(Math.pow(pos.x - startPos.x, 2) + Math.pow(pos.y - startPos.y, 2));
        newShape = {
          type: 'circle',
          x: startPos.x, y: startPos.y, radius: circleRadius,
          color: currentColor, fillColor, lineWidth, opacity,
          rotation: 0, scaleX: 1, scaleY: 1
        };
        break;
      case 'ellipse':
        newShape = {
          type: 'ellipse',
          x: startPos.x + (pos.x - startPos.x) / 2,
          y: startPos.y + (pos.y - startPos.y) / 2,
          radiusX: Math.abs((pos.x - startPos.x) / 2),
          radiusY: Math.abs((pos.y - startPos.y) / 2),
          color: currentColor, fillColor, lineWidth, opacity,
          rotation: 0, scaleX: 1, scaleY: 1
        };
        break;
      case 'triangle':
        newShape = {
          type: 'triangle',
          x1: startPos.x + (pos.x - startPos.x) / 2, y1: startPos.y,
          x2: startPos.x, y2: startPos.y + (pos.y - startPos.y),
          x3: startPos.x + (pos.x - startPos.x), y3: startPos.y + (pos.y - startPos.y),
          color: currentColor, fillColor, lineWidth, opacity,
          rotation: 0, scaleX: 1, scaleY: 1
        };
        break;
      case 'pentagon':
      case 'hexagon':
      case 'octagon':
        newShape = {
          type: tool,
          x1: startPos.x, y1: startPos.y, x2: pos.x, y2: pos.y,
          color: currentColor, fillColor, lineWidth, opacity,
          rotation: 0, scaleX: 1, scaleY: 1
        };
        break;
      case 'cloud':
        newShape = {
          type: 'cloud',
          x1: startPos.x, y1: startPos.y, x2: pos.x, y2: pos.y,
          color: currentColor, lineWidth, opacity,
          rotation: 0, scaleX: 1, scaleY: 1
        };
        break;
      case 'cloudCallout': {
        // Cloud where you PRESSED, arrow head where you RELEASED; text is
        // asked for right after and sizes the cloud.
        setPendingCallout({
          type: 'cloudCallout',
          x: startPos.x, y: startPos.y, width: 140, height: 70,
          pointerX: pos.x, pointerY: pos.y,
          color: currentColor, fillColor: 'transparent', lineWidth, opacity, fontSize,
          rotation: 0, scaleX: 1, scaleY: 1,
        });
        setTextKind('callout');
        setTextPosition({ x: startPos.x, y: startPos.y });
        setIsDrawing(false);
        return;
      }
      case 'callout': {
        // Callout = leader from where you pressed to a text box where you
        // released; the text is asked for right after.
        const bw = Math.max(120, Math.abs(pos.x - startPos.x) || 160);
        const bh = Math.max(44, Math.abs(pos.y - startPos.y) / 2 || 48);
        setPendingCallout({
          type: 'callout',
          x: pos.x, y: pos.y - bh, width: bw, height: bh,
          pointerX: startPos.x, pointerY: startPos.y,
          color: currentColor, fillColor: fillColor === 'transparent' ? '#FFFFFF' : fillColor, lineWidth, opacity,
          rotation: 0, scaleX: 1, scaleY: 1,
        });
        setTextKind('callout');
        setTextPosition({ x: pos.x, y: pos.y });
        setIsDrawing(false);
        return;
      }
      case 'cross':
        const crossSize = Math.sqrt(Math.pow(pos.x - startPos.x, 2) + Math.pow(pos.y - startPos.y, 2));
        newShape = {
          type: 'cross',
          x: startPos.x, y: startPos.y, size: crossSize,
          color: currentColor, lineWidth, opacity,
          rotation: 0, scaleX: 1, scaleY: 1
        };
        break;
      case 'checkmark':
        const checkSize = Math.sqrt(Math.pow(pos.x - startPos.x, 2) + Math.pow(pos.y - startPos.y, 2));
        newShape = {
          type: 'checkmark',
          x: startPos.x, y: startPos.y, size: checkSize,
          color: currentColor, lineWidth, opacity,
          rotation: 0, scaleX: 1, scaleY: 1
        };
        break;
      case 'measure':
        newShape = {
          type: 'measure',
          x1: startPos.x, y1: startPos.y, x2: pos.x, y2: pos.y,
          color: currentColor, lineWidth, opacity, fontSize,
          unit: measurementUnit,
          rotation: 0, scaleX: 1, scaleY: 1
        };
        break;
    }
    
    ctx.globalAlpha = 1;
    ctx.setLineDash([]);
    setIsDrawing(false);

    if (newShape) {
      // Ignore accidental zero-size drags.
      const b = getShapeBounds(newShape);
      if (b.width < 2 && b.height < 2 && !['count', 'stamp'].includes(newShape.type)) { redrawCanvas(); return; }
      addShape(newShape);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new window.Image();
      img.onload = () => {
        const maxSize = 300;
        let width = img.width;
        let height = img.height;
        
        if (width > maxSize || height > maxSize) {
          const ratio = Math.min(maxSize / width, maxSize / height);
          width *= ratio;
          height *= ratio;
        }

        const newImage = {
          imageData: event.target.result,
          x: 100,
          y: 100,
          width,
          height,
          opacity: 1,
          rotation: 0,
          scaleX: 1,
          scaleY: 1
        };

        setInsertedImages(prev => [...prev, newImage]);
        setSelectedImageIndex(insertedImages.length);
        setShowTransformPanel(true);
        saveToHistory();
        setRenderTick((t) => t + 1);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
    
    // Reset input
    e.target.value = '';
  };

const handleTextSubmit = () => {
  if (!textPosition) return;
  if (textKind === 'callout') {
    if (pendingCallout) {
      let sized = { ...pendingCallout, text: textInput.trim(), fontSize };
      if (pendingCallout.type === 'cloudCallout' && overlayCtx) {
        const { w, h } = cloudCalloutSize(overlayCtx, textInput.trim(), fontSize);
        sized = { ...sized, width: w, height: h };
      }
      addShape(sized);
      setPendingCallout(null);
    }
    setTextInput('');
    setTextPosition(null);
    return;
  }
  if (!textInput.trim()) return;
  if (textKind === 'note') {
    addShape({
      type: 'note', x: textPosition.x, y: textPosition.y, width: 190, height: 120,
      text: textInput, author: userInfo?.name || '', fontSize: 13,
      color: '#F9A825', fillColor: '#FFF59D', opacity, rotation: 0, scaleX: 1, scaleY: 1,
    });
  } else {
    addShape({
      type: 'text', x: textPosition.x, y: textPosition.y, text: textInput,
      fontSize, fontFamily: 'Arial', color: currentColor, opacity, rotation: 0, scaleX: 1, scaleY: 1,
    });
  }
  setTextInput('');
  setTextPosition(null);
};



  const handleDownload = () => {
    if (!canvasRef.current) return;
    
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvasRef.current.width;
    tempCanvas.height = canvasRef.current.height;
    const tempCtx = tempCanvas.getContext('2d');
    
    // Draw main canvas
    tempCtx.drawImage(canvasRef.current, 0, 0);
    
    // Draw overlay (shapes and images)
    if (overlayCanvasRef.current) {
      tempCtx.drawImage(overlayCanvasRef.current, 0, 0);
    }
    
    const link = document.createElement('a');
    link.download = `${documentName || 'document'}_issue.png`;
    link.href = tempCanvas.toDataURL('image/png', 1.0);
    link.click();
  };

  // Is the markup different from what was loaded (or last saved)? A null
  // baseline means no document ever finished loading, so there is nothing
  // of anyone's to lose by saying no.
  const isMarkupDirty = () =>
    isDirtyAgainst(baselineRef.current, signatureFrom(pdfPage, shapes, insertedImages));

  const handleSaveIssue = async ({ onlyIfDirty = false } = {}) => {
    if (!canvasRef.current || !onSaveIssue) return "unchanged";
    // Nothing drawn since the document loaded / the last save: the host
    // asked for a save that would only file another copy of the picture
    // already on the issue. Say so and do nothing. A pasted image is a
    // NEW DOCUMENT, not markup on the old one, so it lands here as
    // "unchanged" and is not attached a second time.
    if (onlyIfDirty && !isMarkupDirty()) return "unchanged";

    setIsSaving(true);
    try {
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvasRef.current.width;
      tempCanvas.height = canvasRef.current.height;
      const tempCtx = tempCanvas.getContext('2d');
      
      tempCtx.drawImage(canvasRef.current, 0, 0);
      if (overlayCanvasRef.current) {
        tempCtx.drawImage(overlayCanvasRef.current, 0, 0);
      }
      
      // Tainted canvas (cross-origin base without CORS) → no raster
      // export, but the vector save below must still go through.
      let issueData = null;
      try { issueData = tempCanvas.toDataURL('image/png', 1.0); } catch (e) { issueData = null; }
      // issueObjects schema wants objectType from a fixed enum; the raw
      // shape rides along under .data and is what gets re-hydrated.
      const shapeObjectType = (t) =>
        t === 'text' ? 'text'
          : (t === 'leftArrow' || t === 'doubleArrow' || t === 'arrow') ? 'arrow'
          : (t === 'measure' || t === 'perimeter' || t === 'area' || t === 'count') ? 'measurement'
          : t === 'cloud' ? 'cloud'
          : (t === 'callout' || t === 'cloudCallout') ? 'callout'
          : t === 'freehand' ? 'freehand'
          : t === 'stamp' ? 'stamp'
          : t === 'note' ? 'callout'
          : 'shape';
      const issueInfo = {
        documentName,
        createdBy: userInfo?.name || 'Anonymous',
        createdByRole: userInfo?.role || 'User',
        createdAt: new Date().toISOString(),
        issueImage: issueData,
        // Vector state — consumers persist these as issueObjects/canvasState.
        //
        // EVERY sheet's markup, not just the one on screen: someone who
        // marked up three sheets and pressed Save used to keep the third
        // and lose the other two. Each shape carries its `page`, and the
        // loader above filters by it, so re-opening puts each drawing
        // back on the sheet it was drawn on. Shapes saved before this
        // have no page and are treated as sheet 1.
        objects: allPageShapes().map((sh) => ({ objectType: shapeObjectType(sh.type), data: sh })),
        canvasState: {
          width: canvasRef.current.width,
          height: canvasRef.current.height,
          scale: 1,
          // Which sheet the raster above is of, and how many there are.
          page: pdfPage,
          pageCount: pdfPageCount,
        },
      };
      
      const savedUrl = await onSaveIssue(issueInfo);
      setIssueUrl(savedUrl);
      // What was just written IS the new baseline.
      baselineRef.current = signatureFrom(pdfPage, shapes, insertedImages);
      if (!embedded) alert('Markup saved.');
      return true;
    } catch (error) {
      console.error('Error saving issue:', error);
      if (!embedded) alert('Failed to save markup. Please try again.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Expose the save to the host page (page-level Save button).
  // The host passes { onlyIfDirty: true } from Save-all / Submit, which
  // run every mounted pane's saver whether or not that pane was touched;
  // the tool's own Save button passes nothing and always writes.
  useEffect(() => {
    if (!saveRef) return undefined;
    saveRef.current = (opts) => handleSaveIssue(opts);
    return () => { if (saveRef) saveRef.current = null; };
  });

  const copyIssueUrl = () => {
    if (issueUrl) {
      navigator.clipboard.writeText(issueUrl);
      alert('Issue URL copied to clipboard!');
    }
  };

  const handleZoomIn = () => setScale(s => Math.min(s + 0.1, 3));
  const handleZoomOut = () => setScale(s => Math.max(s - 0.1, 0.3));
  
  const handleFitToScreen = () => {
    if (!containerRef.current || !canvasSize.width) return;
    const container = containerRef.current;
    const padding = 100;
    const scaleX = (container.clientWidth - padding) / canvasSize.width;
    const scaleY = (container.clientHeight - padding) / canvasSize.height;
    setScale(Math.min(scaleX, scaleY, 1));
  };

  // Update selected element transforms from panel
  const updateSelectedTransform = (property, value) => {
    if (selectedImageIndex !== null) {
      setInsertedImages(prev => {
        const newImages = [...prev];
        newImages[selectedImageIndex] = { ...newImages[selectedImageIndex], [property]: value };
        return newImages;
      });
    } else if (selectedShapeIndex !== null) {
      setShapes(prev => {
        const newShapes = [...prev];
        newShapes[selectedShapeIndex] = { ...newShapes[selectedShapeIndex], [property]: value };
        return newShapes;
      });
    }
  };

  const getSelectedElement = () => {
    if (selectedImageIndex !== null) return insertedImages[selectedImageIndex];
    if (selectedShapeIndex !== null) return shapes[selectedShapeIndex];
    return null;
  };

  // Layer ordering functions
  const bringToFront = () => {
    if (selectedShapeIndex !== null && selectedShapeIndex < shapes.length - 1) {
      setShapes(prev => {
        const newShapes = [...prev];
        const [item] = newShapes.splice(selectedShapeIndex, 1);
        newShapes.push(item);
        setSelectedShapeIndex(newShapes.length - 1);
        return newShapes;
      });
    } else if (selectedImageIndex !== null && selectedImageIndex < insertedImages.length - 1) {
      setInsertedImages(prev => {
        const newImages = [...prev];
        const [item] = newImages.splice(selectedImageIndex, 1);
        newImages.push(item);
        setSelectedImageIndex(newImages.length - 1);
        return newImages;
      });
    }
  };

  const sendToBack = () => {
    if (selectedShapeIndex !== null && selectedShapeIndex > 0) {
      setShapes(prev => {
        const newShapes = [...prev];
        const [item] = newShapes.splice(selectedShapeIndex, 1);
        newShapes.unshift(item);
        setSelectedShapeIndex(0);
        return newShapes;
      });
    } else if (selectedImageIndex !== null && selectedImageIndex > 0) {
      setInsertedImages(prev => {
        const newImages = [...prev];
        const [item] = newImages.splice(selectedImageIndex, 1);
        newImages.unshift(item);
        setSelectedImageIndex(0);
        return newImages;
      });
    }
  };

  const bringForward = () => {
    if (selectedShapeIndex !== null && selectedShapeIndex < shapes.length - 1) {
      setShapes(prev => {
        const newShapes = [...prev];
        [newShapes[selectedShapeIndex], newShapes[selectedShapeIndex + 1]] =
          [newShapes[selectedShapeIndex + 1], newShapes[selectedShapeIndex]];
        setSelectedShapeIndex(selectedShapeIndex + 1);
        return newShapes;
      });
    } else if (selectedImageIndex !== null && selectedImageIndex < insertedImages.length - 1) {
      setInsertedImages(prev => {
        const newImages = [...prev];
        [newImages[selectedImageIndex], newImages[selectedImageIndex + 1]] =
          [newImages[selectedImageIndex + 1], newImages[selectedImageIndex]];
        setSelectedImageIndex(selectedImageIndex + 1);
        return newImages;
      });
    }
  };

  const sendBackward = () => {
    if (selectedShapeIndex !== null && selectedShapeIndex > 0) {
      setShapes(prev => {
        const newShapes = [...prev];
        [newShapes[selectedShapeIndex], newShapes[selectedShapeIndex - 1]] =
          [newShapes[selectedShapeIndex - 1], newShapes[selectedShapeIndex]];
        setSelectedShapeIndex(selectedShapeIndex - 1);
        return newShapes;
      });
    } else if (selectedImageIndex !== null && selectedImageIndex > 0) {
      setInsertedImages(prev => {
        const newImages = [...prev];
        [newImages[selectedImageIndex], newImages[selectedImageIndex - 1]] =
          [newImages[selectedImageIndex - 1], newImages[selectedImageIndex]];
        setSelectedImageIndex(selectedImageIndex - 1);
        return newImages;
      });
    }
  };

  if (!isOpen) return null;

  const selectedElement = getSelectedElement();

  // SVG Icons as components
  const MousePointerIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z"/>
      <path d="M13 13l6 6"/>
    </svg>
  );

  const LineIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <line x1="4" y1="20" x2="20" y2="4"/>
    </svg>
  );

  const outerStyle = embedded
    ? {
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'stretch',
      }
    : {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      };

  const innerStyle = embedded
    ? {
        backgroundColor: 'white',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
      }
    : {
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        width: '98%',
        height: '98%',
        display: 'flex',
        flexDirection: 'column',
      };

  return (
    <div style={outerStyle}>
      <div style={innerStyle}>
        {/* Header */}
        {!hideHeader && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span>📝 {documentName}</span>
              {pdfPageCount > 1 && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500 }}>
                  <button
                    type="button"
                    onClick={() => flipPage(pdfPage - 1)}
                    disabled={pdfPage <= 1}
                    title="Previous sheet"
                    style={{
                      border: '1px solid #d1d5db', background: '#fff', borderRadius: 4,
                      width: 26, height: 26, cursor: pdfPage <= 1 ? 'default' : 'pointer',
                      opacity: pdfPage <= 1 ? 0.45 : 1, lineHeight: 1,
                    }}
                  >‹</button>
                  <span style={{ color: '#374151', minWidth: 78, textAlign: 'center' }}>
                    Sheet {pdfPage} / {pdfPageCount}
                  </span>
                  <button
                    type="button"
                    onClick={() => flipPage(pdfPage + 1)}
                    disabled={pdfPage >= pdfPageCount}
                    title="Next sheet"
                    style={{
                      border: '1px solid #d1d5db', background: '#fff', borderRadius: 4,
                      width: 26, height: 26, cursor: pdfPage >= pdfPageCount ? 'default' : 'pointer',
                      opacity: pdfPage >= pdfPageCount ? 0.45 : 1, lineHeight: 1,
                    }}
                  >›</button>
                </span>
              )}
            </h2>
            {userInfo && (
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                Editing as: <strong>{userInfo.name}</strong> ({userInfo.role || 'User'})
                {issueUrl && (
                  <span style={{ marginLeft: '12px', color: '#10b981' }}>
                    ✓ Saved - <button onClick={copyIssueUrl} style={{
                      background: 'none', border: 'none', color: '#3b82f6',
                      textDecoration: 'underline', cursor: 'pointer', fontSize: '12px'
                    }}>Copy Link</button>
                  </span>
                )}
              </div>
            )}
          </div>
          {onClose && (
          <button onClick={onClose} style={{
            padding: '6px 16px',
            border: '1px solid #d1d5db',
            background: 'white',
            cursor: 'pointer',
            borderRadius: '4px',
            fontWeight: '500'
          }}>
            ✕ Close
          </button>
          )}
        </div>
        )}

        {/* Toolbar — editors only. Bluebeam-style groups with flyouts:
            Select/Pan · Pen/Highlighter/Eraser · Text/Note/Callout/Stamp ·
            Lines ▾ · Shapes ▾ · Measure ▾ · colour swatches + Style ▾ ·
            Undo/Redo · Zoom · View ▾ · Image · Save */}
        {canEdit && (
        <div style={{
          display: 'flex', gap: '6px', padding: '6px 8px', borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb', flexWrap: 'wrap', alignItems: 'center', position: 'relative', zIndex: 5,
        }}>
          <TbGroup>
            <TbBtn active={tool === 'select'} onClick={() => pickTool('select')} title="Select — move, resize, rotate (V)"><MousePointerIcon /></TbBtn>
            <TbBtn active={tool === 'pan'} onClick={() => pickTool('pan')} title="Pan (H)">✋</TbBtn>
          </TbGroup>

          <TbGroup>
            <TbBtn active={tool === 'pen'} onClick={() => pickTool('pen')} title="Pen — freehand (P)">✏️</TbBtn>
            <TbBtn active={tool === 'highlighter'} onClick={() => pickTool('highlighter')} title="Highlighter — freehand (G)">🖍️</TbBtn>
            <TbBtn active={tool === 'eraser'} onClick={() => pickTool('eraser')} title="Eraser — click or drag over markup to remove it (X)">🧽</TbBtn>
          </TbGroup>

          <TbGroup>
            <TbBtn active={tool === 'text'} onClick={() => pickTool('text')} title="Text (T)"><b style={{ fontFamily: 'serif' }}>T</b></TbBtn>
            <TbBtn active={tool === 'note'} onClick={() => pickTool('note')} title="Sticky note (N)">🗒️</TbBtn>
            <TbBtn active={tool === 'cloudCallout'} onClick={() => pickTool('cloudCallout')} title="Cloud callout — press where the cloud goes, drag, release where the arrow should point, then type (K)">☁️➜</TbBtn>
            <TbFlyout
              id="stamp" openId={openFlyout} setOpenId={setOpenFlyout}
              active={tool === 'stamp'} title="Stamp — pick one, then click to place (S)"
              label={<span>🔖 {tool === 'stamp' ? stampText : 'Stamp'}</span>}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, minWidth: 260 }}>
                {STAMPS.map((st) => (
                  <button key={st} onClick={() => { setStampText(st); pickTool('stamp'); setOpenFlyout(null); }}
                    style={{ ...flyItemStyle, fontWeight: 700, color: '#C62828', border: '2px solid #C62828', borderRadius: 4, textAlign: 'center' }}>
                    {st}
                  </button>
                ))}
              </div>
              <div style={{ marginTop: 6, fontSize: 10.5, color: '#6b7280' }}>Custom:</div>
              <input placeholder="Type stamp text + Enter" style={{ ...flyInputStyle, width: '100%' }}
                onKeyDown={(e) => { if (e.key === 'Enter' && e.currentTarget.value.trim()) { setStampText(e.currentTarget.value.trim()); pickTool('stamp'); setOpenFlyout(null); } }} />
            </TbFlyout>
          </TbGroup>

          <TbGroup>
            <TbFlyout
              id="lines" openId={openFlyout} setOpenId={setOpenFlyout}
              active={['line', 'arrow', 'doubleArrow', 'polyline'].includes(tool)}
              title="Lines & arrows"
              label={<span>{TOOL_META[tool]?.group === 'lines' ? TOOL_META[tool].icon : '↗'} Lines</span>}
            >
              <FlyList items={['line', 'arrow', 'doubleArrow', 'polyline']} tool={tool} onPick={(t) => { pickTool(t); setOpenFlyout(null); }} />
            </TbFlyout>
            <TbFlyout
              id="shapes" openId={openFlyout} setOpenId={setOpenFlyout}
              active={['rectangle', 'highlightRect', 'ellipse', 'circle', 'polygon', 'cloud', 'triangle', 'cross', 'checkmark'].includes(tool)}
              title="Shapes"
              label={<span>{TOOL_META[tool]?.group === 'shapes' ? TOOL_META[tool].icon : '▭'} Shapes</span>}
            >
              <FlyList items={['rectangle', 'highlightRect', 'ellipse', 'circle', 'cloud', 'polygon', 'triangle', 'checkmark', 'cross']} tool={tool} onPick={(t) => { pickTool(t); setOpenFlyout(null); }} />
            </TbFlyout>
            <TbFlyout
              id="measure" openId={openFlyout} setOpenId={setOpenFlyout}
              active={['measure', 'perimeter', 'area', 'count'].includes(tool)}
              title="Measure"
              label={<span>{TOOL_META[tool]?.group === 'measure' ? TOOL_META[tool].icon : '📐'} Measure</span>}
            >
              <FlyList items={['measure', 'perimeter', 'area', 'count']} tool={tool} onPick={(t) => { pickTool(t); setOpenFlyout(null); }} />
              <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 6, paddingTop: 6, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                <span style={{ color: '#6b7280' }}>Unit</span>
                <select value={measurementUnit} onChange={(e) => setMeasurementUnit(e.target.value)} style={flyInputStyle}>
                  <option value="px">px</option><option value="in">in</option><option value="ft">ft</option>
                  <option value="cm">cm</option><option value="mm">mm</option><option value="m">m</option>
                </select>
                <span style={{ color: '#6b7280' }}>Scale ×</span>
                <input type="number" step="0.01" min="0.01" value={scaleFactor} onChange={(e) => setScaleFactor(Number(e.target.value) || 1)} style={{ ...flyInputStyle, width: 60 }} title="Drawing scale factor (pixels per real pixel)" />
              </div>
              {tool === 'count' && (
                <div style={{ marginTop: 6, fontSize: 11, color: '#6b7280' }}>Next number: <b>{countNext}</b> <button onClick={() => setCountNext(1)} style={{ ...flyItemStyle, display: 'inline', padding: '1px 6px' }}>reset</button></div>
              )}
            </TbFlyout>
          </TbGroup>

          {/* Colour swatches + Style flyout */}
          <TbGroup>
            {SWATCHES.map((c) => (
              <button key={c} onClick={() => setCurrentColor(c)} title={c}
                style={{
                  width: 18, height: 18, borderRadius: '50%', border: currentColor.toUpperCase() === c ? '2px solid #111827' : '1px solid #d1d5db',
                  background: c, cursor: 'pointer', padding: 0, margin: '0 1px', boxShadow: currentColor.toUpperCase() === c ? '0 0 0 2px #fff inset' : 'none',
                }} />
            ))}
            <label title="Custom colour" style={{ position: 'relative', width: 18, height: 18, borderRadius: '50%', border: '1px solid #d1d5db', background: 'conic-gradient(red, yellow, lime, cyan, blue, magenta, red)', cursor: 'pointer', margin: '0 2px', overflow: 'hidden' }}>
              <input type="color" value={currentColor} onChange={(e) => setCurrentColor(e.target.value)} style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }} />
            </label>
            <TbFlyout id="style" openId={openFlyout} setOpenId={setOpenFlyout} title="Style — fill, width, opacity, dash, font" label={<span>Style ▾</span>}>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 10px', alignItems: 'center', fontSize: 11, minWidth: 220 }}>
                <span style={{ color: '#6b7280' }}>Width</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[1, 2, 4, 8].map((w) => (
                    <button key={w} onClick={() => setLineWidth(w)} style={{ ...flyItemStyle, padding: '2px 8px', border: `1px solid ${lineWidth === w ? '#3b82f6' : '#e5e7eb'}`, borderRadius: 4 }}>
                      <span style={{ display: 'inline-block', width: 22, height: w, background: '#111827', verticalAlign: 'middle' }} />
                    </button>
                  ))}
                  <input type="range" min="1" max="20" value={lineWidth} onChange={(e) => setLineWidth(Number(e.target.value))} style={{ width: 60 }} />
                </div>
                <span style={{ color: '#6b7280' }}>Fill</span>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <button onClick={() => applyFill('transparent')} title="No fill" style={{ ...flyItemStyle, width: 18, height: 18, borderRadius: '50%', border: `2px solid ${fillColor === 'transparent' ? '#111827' : '#d1d5db'}`, background: 'linear-gradient(135deg,#fff 45%,#ef4444 47%,#ef4444 53%,#fff 55%)', padding: 0 }} />
                  {SWATCHES.map((c) => (
                    <button key={c} onClick={() => applyFill(c)} title={`${c} at ${Math.round(fillAlpha * 100)}% fill`} style={{ ...flyItemStyle, width: 18, height: 18, borderRadius: '50%', border: fillColor.toUpperCase().startsWith(c.toUpperCase()) ? '2px solid #111827' : '1px solid #d1d5db', background: c, padding: 0 }} />
                  ))}
                  <span title="How see-through the fill is" style={{ color: '#6b7280', fontSize: 10 }}>see-through</span>
                  <input
                    type="range" min="0.1" max="1" step="0.05" value={fillAlpha}
                    onChange={(e) => {
                      const a = Number(e.target.value);
                      setFillAlpha(a);
                      if (fillColor && fillColor !== 'transparent') {
                        setFillColor(withAlpha(fillColor.slice(0, 7), a));
                      }
                    }}
                    style={{ width: 70 }}
                  />
                </div>
                <span style={{ color: '#6b7280' }}>Opacity</span>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="range" min="0.1" max="1" step="0.1" value={opacity} onChange={(e) => setOpacity(Number(e.target.value))} style={{ width: 90 }} />
                  <span>{Math.round(opacity * 100)}%</span>
                </div>
                <span style={{ color: '#6b7280' }}>Line</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[['[]', 'Solid'], ['[8,6]', 'Dashed'], ['[2,4]', 'Dotted']].map(([v, l]) => (
                    <button key={v} onClick={() => setLineDash(JSON.parse(v))} style={{ ...flyItemStyle, padding: '2px 8px', border: `1px solid ${JSON.stringify(lineDash) === JSON.stringify(JSON.parse(v)) ? '#3b82f6' : '#e5e7eb'}`, borderRadius: 4 }}>{l}</button>
                  ))}
                </div>
                <span style={{ color: '#6b7280' }}>Font</span>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="number" min="8" max="96" value={fontSize} onChange={(e) => setFontSize(Number(e.target.value) || 16)} style={{ ...flyInputStyle, width: 60 }} /> px
                </div>
              </div>
            </TbFlyout>
          </TbGroup>

          <TbGroup>
            <TbBtn onClick={undo} disabled={historyStep <= 0} title="Undo (Ctrl+Z)">↶</TbBtn>
            <TbBtn onClick={redo} disabled={historyStep >= history.length - 1} title="Redo (Ctrl+Y)">↷</TbBtn>
          </TbGroup>

          <TbGroup>
            <TbBtn onClick={handleZoomOut} title="Zoom out">−</TbBtn>
            <span style={{ fontSize: 10.5, minWidth: 36, textAlign: 'center' }}>{Math.round(scale * 100)}%</span>
            <TbBtn onClick={handleZoomIn} title="Zoom in">+</TbBtn>
            <TbBtn onClick={handleFitToScreen} title="Fit to screen">⊡</TbBtn>
            <TbFlyout id="view" openId={openFlyout} setOpenId={setOpenFlyout} title="View options" label={<span>View ▾</span>}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11, cursor: 'pointer' }}><input type="checkbox" checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)} /> Show grid</label>
              {showGrid && sheetRef && !sheetGrid.loading && (
                <div data-testid="issue-tool-grid-why" style={{ fontSize: 10, color: sheetGrid.lines.length ? '#2F6FB3' : '#8A6112', margin: '2px 0 0 20px', maxWidth: 200 }}>
                  {sheetGrid.lines.length ? 'Building grid' : `${SHEET_GRID_WHY[sheetGrid.reason] || SHEET_GRID_WHY.unavailable} - showing a drawing grid`}
                </div>
              )}
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11, cursor: 'pointer', marginTop: 4 }}><input type="checkbox" checked={snapToGrid} onChange={(e) => setSnapToGrid(e.target.checked)} /> Snap to grid</label>
              <button onClick={() => fileInputRef.current?.click()} style={{ ...flyItemStyle, marginTop: 8, borderTop: '1px solid #e5e7eb', paddingTop: 6 }}>🖼️ Insert image…</button>
              <button onClick={handleDownload} style={flyItemStyle}>📥 Download PNG</button>
            </TbFlyout>
          </TbGroup>

          <span style={{ flex: 1 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'stretch' }}>
            {fullscreen && (
              <button onClick={fullscreen.toggle} title={fullscreen.active ? 'Exit full screen (Esc)' : 'Full screen - canvas with comments and activity on the side'} style={{
                padding: '5px 14px', border: 'none', borderRadius: 4, cursor: 'pointer',
                backgroundColor: '#3b82f6', color: 'white', fontSize: 11.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center',
              }}>
                <span style={{ fontSize: 13, lineHeight: 1 }}>{fullscreen.active ? '🗗' : '⛶'}</span>{fullscreen.active ? 'Exit full screen' : 'Full screen'}
              </button>
            )}
            <button onClick={() => handleSaveIssue()} disabled={isSaving} style={{
              padding: '5px 14px', border: 'none', borderRadius: 4, cursor: isSaving ? 'not-allowed' : 'pointer',
              backgroundColor: '#3b82f6', color: 'white', fontSize: 11.5, fontWeight: 600, opacity: isSaving ? 0.6 : 1,
            }}>
              {isSaving ? 'Saving…' : '💾 Save markup'}
            </button>
          </div>

          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
        </div>
        )}

{/* Transform Panel - Shows when element is selected */}
{canEdit && showTransformPanel && selectedElement && (
  <div
    style={{
      display: 'flex',
      gap: '12px',
      padding: '8px 16px',
      borderBottom: '1px solid #e5e7eb',
      backgroundColor: '#f0f9ff',
      alignItems: 'center'
    }}
  >
    <span style={{ fontSize: '11px', fontWeight: '600', color: '#1e40af' }}>
      Transform:
    </span>

    {/* Rotation */}
    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
      <label style={{ fontSize: '10px', color: '#6b7280' }}>Rotation:</label>
      <input
        type="number"
        value={Math.round(selectedElement.rotation || 0)}
        onChange={(e) =>
          updateSelectedTransform('rotation', Number(e.target.value))
        }
        style={{
          width: '50px',
          padding: '2px 4px',
          border: '1px solid #d1d5db',
          borderRadius: '3px',
          fontSize: '10px'
        }}
      />
      <span style={{ fontSize: '10px' }}>°</span>
    </div>

    {/* Scale X */}
    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
      <label style={{ fontSize: '10px', color: '#6b7280' }}>Scale X:</label>
      <input
        type="number"
        step="0.1"
        min="0.1"
        value={(selectedElement.scaleX || 1).toFixed(1)}
        onChange={(e) =>
          updateSelectedTransform('scaleX', Number(e.target.value))
        }
        style={{
          width: '50px',
          padding: '2px 4px',
          border: '1px solid #d1d5db',
          borderRadius: '3px',
          fontSize: '10px'
        }}
      />
    </div>

    {/* Scale Y */}
    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
      <label style={{ fontSize: '10px', color: '#6b7280' }}>Scale Y:</label>
      <input
        type="number"
        step="0.1"
        min="0.1"
        value={(selectedElement.scaleY || 1).toFixed(1)}
        onChange={(e) =>
          updateSelectedTransform('scaleY', Number(e.target.value))
        }
        style={{
          width: '50px',
          padding: '2px 4px',
          border: '1px solid #d1d5db',
          borderRadius: '3px',
          fontSize: '10px'
        }}
      />
    </div>

    {/* Opacity */}
    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
      <label style={{ fontSize: '10px', color: '#6b7280' }}>Opacity:</label>
      <input
        type="range"
        min="0.1"
        max="1"
        step="0.1"
        value={selectedElement.opacity || 1}
        onChange={(e) =>
          updateSelectedTransform('opacity', Number(e.target.value))
        }
        style={{ width: '60px' }}
      />
      <span style={{ fontSize: '10px' }}>
        {Math.round((selectedElement.opacity || 1) * 100)}%
      </span>
    </div>

    {/* 🔤 Font Size (TEXT ONLY) */}
    {selectedElement.type === 'text' && (
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
        <label style={{ fontSize: '10px', color: '#6b7280' }}>
          Font Size:
        </label>
        <input
          type="number"
          min="8"
          max="200"
          value={selectedElement.fontSize || 16}
          onChange={(e) =>
            updateSelectedTransform('fontSize', Number(e.target.value))
          }
          style={{
            width: '50px',
            padding: '2px 4px',
            border: '1px solid #d1d5db',
            borderRadius: '3px',
            fontSize: '10px'
          }}
        />
      </div>
    )}

    <div style={{ width: '1px', height: '24px', backgroundColor: '#d1d5db', margin: '0 8px' }} />

    {/* Layer Ordering */}
    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
      <label style={{ fontSize: '10px', fontWeight: '500', color: '#6b7280', marginRight: '4px' }}>Layer:</label>
      <button
        onClick={bringToFront}
        disabled={
          (selectedShapeIndex !== null && selectedShapeIndex >= shapes.length - 1) ||
          (selectedImageIndex !== null && selectedImageIndex >= insertedImages.length - 1)
        }
        style={{
          padding: '4px 8px',
          border: '1px solid #d1d5db',
          borderRadius: '3px',
          cursor: 'pointer',
          backgroundColor: 'white',
          fontSize: '11px',
          opacity: ((selectedShapeIndex !== null && selectedShapeIndex >= shapes.length - 1) ||
                    (selectedImageIndex !== null && selectedImageIndex >= insertedImages.length - 1)) ? 0.4 : 1
        }}
        title="Bring to Front"
      >
        ⬆️⬆️
      </button>
      <button
        onClick={bringForward}
        disabled={
          (selectedShapeIndex !== null && selectedShapeIndex >= shapes.length - 1) ||
          (selectedImageIndex !== null && selectedImageIndex >= insertedImages.length - 1)
        }
        style={{
          padding: '4px 8px',
          border: '1px solid #d1d5db',
          borderRadius: '3px',
          cursor: 'pointer',
          backgroundColor: 'white',
          fontSize: '11px',
          opacity: ((selectedShapeIndex !== null && selectedShapeIndex >= shapes.length - 1) ||
                    (selectedImageIndex !== null && selectedImageIndex >= insertedImages.length - 1)) ? 0.4 : 1
        }}
        title="Bring Forward"
      >
        ⬆️
      </button>
      <button
        onClick={sendBackward}
        disabled={
          (selectedShapeIndex !== null && selectedShapeIndex <= 0) ||
          (selectedImageIndex !== null && selectedImageIndex <= 0)
        }
        style={{
          padding: '4px 8px',
          border: '1px solid #d1d5db',
          borderRadius: '3px',
          cursor: 'pointer',
          backgroundColor: 'white',
          fontSize: '11px',
          opacity: ((selectedShapeIndex !== null && selectedShapeIndex <= 0) ||
                    (selectedImageIndex !== null && selectedImageIndex <= 0)) ? 0.4 : 1
        }}
        title="Send Backward"
      >
        ⬇️
      </button>
      <button
        onClick={sendToBack}
        disabled={
          (selectedShapeIndex !== null && selectedShapeIndex <= 0) ||
          (selectedImageIndex !== null && selectedImageIndex <= 0)
        }
        style={{
          padding: '4px 8px',
          border: '1px solid #d1d5db',
          borderRadius: '3px',
          cursor: 'pointer',
          backgroundColor: 'white',
          fontSize: '11px',
          opacity: ((selectedShapeIndex !== null && selectedShapeIndex <= 0) ||
                    (selectedImageIndex !== null && selectedImageIndex <= 0)) ? 0.4 : 1
        }}
        title="Send to Back"
      >
        ⬇️⬇️
      </button>
    </div>

    {/* Delete */}
    <button
      onClick={() => {
        if (selectedImageIndex !== null) {
          setInsertedImages(prev =>
            prev.filter((_, i) => i !== selectedImageIndex)
          );
          setSelectedImageIndex(null);
        } else if (selectedShapeIndex !== null) {
          setShapes(prev =>
            prev.filter((_, i) => i !== selectedShapeIndex)
          );
          setSelectedShapeIndex(null);
        }
        setShowTransformPanel(false);
      }}
      style={{
        padding: '4px 8px',
        border: 'none',
        borderRadius: '3px',
        cursor: 'pointer',
        backgroundColor: '#ef4444',
        color: 'white',
        fontSize: '10px',
        marginLeft: 'auto'
      }}
    >
      🗑️ Delete
    </button>
  </div>
)}


        {/* Text / Note / Callout entry */}
        {textPosition && (
          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            backgroundColor: 'white', padding: '14px 16px', borderRadius: '8px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)', zIndex: 10, width: 300
          }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 8 }}>
              {textKind === 'note' ? '🗒️ Sticky note' : textKind === 'callout' ? '☁️ Cloud callout text' : 'T Text'}
            </div>
            <textarea value={textInput} onChange={(e) => setTextInput(e.target.value)} rows={textKind === 'text' ? 2 : 4}
              placeholder={textKind === 'note' ? 'Write the note…' : textKind === 'callout' ? 'What should the callout say?' : 'Enter text…'} autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleTextSubmit(); } if (e.key === 'Escape') { setTextPosition(null); setTextInput(''); setPendingCallout(null); } }}
              style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 4, marginBottom: 8, width: '100%', display: 'block', fontSize: 13, resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {textKind !== 'note' && (
                <label style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                  Size <input type="number" min="8" max="96" value={fontSize} onChange={(e) => setFontSize(Number(e.target.value) || 16)} style={{ width: 52, padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: 3 }} />
                </label>
              )}
              <span style={{ flex: 1 }} />
              <button onClick={() => { setTextPosition(null); setTextInput(''); setPendingCallout(null); }} style={{ padding: '6px 12px', backgroundColor: '#e5e7eb', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleTextSubmit} style={{ padding: '6px 14px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
                {textKind === 'callout' ? 'Place callout' : 'Add'}
              </button>
            </div>
            <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 6 }}>Enter to add · Shift+Enter for a new line · Esc to cancel</div>
          </div>
        )}

        {/* Canvas Area */}
        <div ref={containerRef} style={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: '#e5e7eb',
          position: 'relative',
          cursor: tool === 'pan' ? 'grab' : tool === 'select' ? 'default' : tool === 'eraser' ? 'cell' : 'crosshair'
        }}>
          <div style={{
            width: `${canvasSize.width * scale + 100}px`,
            height: `${canvasSize.height * scale + 100}px`,
            minWidth: '100%',
            minHeight: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '50px'
          }}>
            <div style={{
              position: 'relative',
              display: 'inline-block',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              transform: `scale(${scale})`,
              transformOrigin: 'center center'
            }}>
              <canvas
                ref={canvasRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onDoubleClick={handleDoubleClick}
                style={{
                  backgroundColor: 'white',
                  display: 'block',
                  width: `${canvasSize.width}px`,
                  height: `${canvasSize.height}px`
                }}
              />
              <canvas
                ref={overlayCanvasRef}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  pointerEvents: 'none',
                  width: `${canvasSize.width}px`,
                  height: `${canvasSize.height}px`
                }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '6px 16px',
          backgroundColor: '#f9fafb',
          borderTop: '1px solid #e5e7eb',
          fontSize: '11px',
          color: '#6b7280',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>
            {MULTI_POINT_TOOLS.includes(tool)
              ? `Click to add points · double-click or Enter to finish${tool === 'polygon' || tool === 'area' ? ' · click the first point to close' : ''} · Esc cancels`
              : tool === 'select' ? 'Click a markup to move / resize / rotate · Delete removes it · Ctrl+Wheel zooms'
              : tool === 'cloudCallout' || tool === 'callout' ? 'Press where the cloud goes, drag to the destination, release, then type — the arrow points at the destination'
              : tool === 'stamp' ? `Click to place "${stampText}"`
              : tool === 'count' ? `Click to place marker #${countNext}`
              : 'Drag to draw · Esc deselects · Ctrl+Z undo · Ctrl+Wheel zoom'}
          </span>
          <span style={{ fontWeight: '500' }}>
            {canvasSize.width}×{canvasSize.height}px •
            Zoom: {Math.round(scale * 100)}% •
            Shapes: {shapes.length} •
            Images: {insertedImages.length}
          </span>
        </div>
      </div>
    </div>
  );
}